import { Component, Element, State, Prop, Method, Event, EventEmitter, h } from '@stencil/core';
import { ServiceContainer, ComponentConfig } from '../../services/ServiceContainer';
import { POWERED_BY_JAAK_FALLBACK } from './watermark-fallback';
import { ICameraService } from '../../services/interfaces/ICameraService';
import { IDetectionService, DetectionBox } from '../../services/interfaces/IDetectionService';
import { IStateManager } from '../../services/interfaces/IStateManager';
import { IEventBus } from '../../services/interfaces/IEventBus';
import { ITracingService } from '../../services/interfaces/ITracingService';
import { IMetricsService } from '../../services/interfaces/IMetricsService';
import { CameraInfoResponse, CapturedImagesResponse, StatusResponse } from '../../types/component-types';
import { LicenseValidationService, LicenseValidationError } from '../../services/LicenseValidationService';

@Component({
  tag: 'jaak-stamps',
  styleUrl: 'my-component.css',
  assetsDirs: ['../../assets'],
  shadow: true,
})
export class JaakStamps {
  @Element() el!: HTMLElement;
  @Prop() debug: boolean = false;
  @Prop() alignmentTolerance: number = 15;
  @Prop() maskSize: number = 90;
  @Prop() cropMargin: number = 20;
  @Prop() useDocumentClassification: boolean = false;
  @Prop() useDocumentDetector: boolean = true;
  @Prop() preferredCamera: 'auto' | 'front' | 'back' = 'auto';
  @Prop() captureDelay: number = 1500;
  @Prop() enableBackDocumentTimer: boolean = false;
  @Prop() backDocumentTimerDuration: number = 20;
  @Prop() telemetryCollectorUrl?: string = 'https://collector.jaak.ai/v1/traces';
  @Prop() metricsCollectorUrl?: string = 'https://collector.jaak.ai/v1/metrics';
  @Prop() enableTelemetry: boolean = true;
  @Prop() enableMetrics: boolean = true;
  @Prop() customerId?: string;
  @Prop() tenantId?: string;
  @Prop() environment?: string = 'production';
  @Prop() propagateTraceHeaderCorsUrls?: string; // Comma-separated URLs or regex patterns
  @Prop() metricsExportIntervalMillis?: number = 60000; // Export metrics every 60 seconds
  @Prop() license?: string; // License key for validation
  @Prop() licenseEnvironment?: 'dev' | 'qa' | 'sandbox' | 'prod' = 'prod'; // Environment for license validation
  @Prop() traceId?: string; // Optional trace ID for tracking
  @Prop() appId?: string = 'jaak-stamps-web'; // Application ID for license validation

  @Event() captureCompleted: EventEmitter<any>;
  @Event() isReady: EventEmitter<boolean>;
  @Event() traceIdGenerated: EventEmitter<{traceId: string}>;

  // State derived from services
  @State() detectionBoxes: Array<{x: number, y: number, w: number, h: number, score: number}> = [];
  @State() sideAlignment: { top: boolean; right: boolean; bottom: boolean; left: boolean } = {
    top: false, right: false, bottom: false, left: false
  };
  @State() isMaskReady: boolean = false;
  @State() shouldMirrorVideo: boolean = true;
  @State() showCameraSelector: boolean = false;
  @State() isSwitchingCamera: boolean = false;
  @State() hasDocumentDetected: boolean = false;
  @State() cameraInfoWithAutofocus: {
    availableCameras: Array<{id: string, label: string, selected: boolean, hasAutofocus: boolean}>;
    selectedCameraId: string | null;
    deviceType: string;
    isMultipleCamerasAvailable: boolean;
    preferredFacing: 'environment' | 'user' | null;
  } = {
    availableCameras: [],
    selectedCameraId: null,
    deviceType: 'desktop',
    isMultipleCamerasAvailable: false,
    preferredFacing: null
  };
  @State() currentStatus: {
    message: string;
    description?: string;
    type: 'initializing' | 'ready' | 'loading' | 'active' | 'error';
    isInitialized: boolean;
  } = {
    message: 'Preparando capturador...',
    description: 'Configurando herramientas de captura',
    type: 'initializing',
    isInitialized: false
  };
  @State() performanceData = {
    fps: 0,
    inferenceTime: 0,
    memoryUsage: 0,
    onnxLoadTime: 0,
    frameProcessingTime: 0,
    totalDetections: 0,
    successfulDetections: 0,
    detectionRate: 0
  };
  @State() backDocumentTimerRemaining: number = 0;
  @State() showManualCaptureButton: boolean = false;
  @State() performanceDegradedMode: boolean = false; // Auto-switched to manual mode due to performance
  @State() showPerformanceMessage: boolean = false; // Show performance message temporarily
  @State() captureStateVersion: number = 0; // Triggers re-render when StateManager changes
  @State() processingButton: 'capture-front' | 'capture-back' | 'skip-back' | null = null; // Track which button is processing
  @State() licenseValid: boolean = true; // License validation status
  @State() licenseError: string | null = null; // License validation error message
  @State() classificationDisabled: boolean = false; // Classification disabled at runtime due to errors

  // Services
  private licenseValidationService: LicenseValidationService | null = null;
  private serviceContainer!: ServiceContainer;
  private eventBus!: IEventBus;
  private stateManager!: IStateManager;
  private cameraService!: ICameraService;
  private detectionService!: IDetectionService;
  private tracingService!: ITracingService | null;
  private metricsService!: IMetricsService | null;


  // UI References
  private videoRef?: HTMLVideoElement;
  private detectionContainer?: HTMLDivElement;
  private videoStream?: MediaStream;
  private animationId?: number;

  // Detection state
  private lastDetectedBox?: DetectionBox;
  private startTime?: number;
  private hasScreenshotTaken: boolean = false;
  private alignmentStartTime?: number;
  private alignmentTimer?: number;
  private backDocumentTimer?: number;
  private orientationTimer?: number;

  // Performance monitoring
  private performanceMetrics = {
    fps: 0,
    inferenceTime: 0,
    memoryUsage: 0,
    onnxLoadTime: 0,
    frameProcessingTime: 0,
    totalDetections: 0,
    successfulDetections: 0,
    detectionRate: 0,
    lastUpdateTime: 0
  };
  private performanceUpdateInterval?: number;

  // Performance optimization with adaptive frame skipping
  private frameSkipCounter: number = 0;
  private readonly BASE_FRAME_SKIP: number = 2;
  private readonly MAX_FRAME_SKIP: number = 8;
  private consecutiveFailures: number = 0;
  private readonly MAX_FAILURES: number = 30;
  private lastInferenceTime: number = 0;
  private readonly MIN_INFERENCE_INTERVAL: number = 50;
  private readonly MOBILE_MIN_INFERENCE_INTERVAL: number = 100; // Higher interval for mobile to prevent slow motion
  private performanceHistory: number[] = [];
  private readonly PERFORMANCE_HISTORY_SIZE: number = 10;

  // Sistema simplificado de monitoreo de rendimiento
  private readonly PERFORMANCE_THRESHOLD_MS: number = 2000; // Umbral único para todos los frames
  private readonly SLOW_FRAMES_TO_TRIGGER: number = 2; // Cuántos frames lentos consecutivos antes de cambiar
  private slowFrameCount: number = 0; // Contador de frames consecutivos lentos
  private processedFramesCount: number = 0; // Contador total de frames procesados
  private hasAutoSwitchedToManual: boolean = false;
  private _manualModeLoggedOnce: boolean = false;
  private readonly VIDEO_DIAGNOSTIC_EVENTS = [
    'loadstart',
    'loadedmetadata',
    'loadeddata',
    'canplay',
    'playing',
    'pause',
    'stalled',
    'suspend',
    'abort',
    'emptied',
  ];


  // Canvas pool for optimized screenshot capture
  private canvasPool: HTMLCanvasElement[] = [];
  private readonly MAX_CANVAS_POOL_SIZE = 3;

  async componentWillLoad() {
    if (this.debug) {
      console.log('[JAAK-DEBUG] componentWillLoad() - Props at initialization:');
      console.log('[JAAK-DEBUG]   useDocumentDetector:', this.useDocumentDetector, '(type:', typeof this.useDocumentDetector, ')');
      console.log('[JAAK-DEBUG]   useDocumentClassification:', this.useDocumentClassification);
      console.log('[JAAK-DEBUG]   debug:', this.debug);
      console.log('[JAAK-DEBUG]   license:', this.license ? this.license.substring(0, 8) + '...' : 'EMPTY');
      console.log('[JAAK-DEBUG]   licenseEnvironment:', this.licenseEnvironment);
      console.log('[JAAK-DEBUG]   alignmentTolerance:', this.alignmentTolerance);
      console.log('[JAAK-DEBUG]   maskSize:', this.maskSize);
      console.log('[JAAK-DEBUG]   captureDelay:', this.captureDelay);
      console.log('[JAAK-DEBUG]   preferredCamera:', this.preferredCamera);
      console.log('[JAAK-DEBUG]   appId:', this.appId);
    }

    // License is required - always validate
    // Wait a bit to ensure props are set from attributes
    await new Promise(resolve => setTimeout(resolve, 50));

    if (this.debug) {
      console.log('[JAAK-DEBUG] Props AFTER 50ms wait:');
      console.log('[JAAK-DEBUG]   useDocumentDetector:', this.useDocumentDetector, '(type:', typeof this.useDocumentDetector, ')');
    }

    this.updateStatus('Validando licencia...', 'Verificando autorización de uso', 'initializing');
    await this.validateLicense();

    // If license validation failed, stop initialization
    if (!this.licenseValid) {
      if (this.debug) {
        console.error('[JAAK-DEBUG] License validation FAILED. Stopping initialization.');
      }
      return;
    }

    if (this.debug) {
      console.log('[JAAK-DEBUG] License validation OK');
    }
  }

  async componentDidLoad() {
    try {
      // If license validation failed, stop initialization
      if (!this.licenseValid) {
        return;
      }

      this.updateStatus('Preparando capturador...', 'Configurando herramientas de captura', 'initializing');
      await this.initializeServices();

      this.updateStatus('Preparando funciones...', 'Configurando herramientas de trabajo', 'initializing');
      await this.setupEventListeners();

      this.updateStatus('Configurando cámara...', 'Detectando dispositivos disponibles', 'initializing');
      await this.initializeComponent();

      if (this.debug) {
        this.initializePerformanceMonitor();
      }
    } catch (error) {
      this.updateStatus('Error de inicialización', error.message, 'error');
    }
  }

  private async initializeServices(): Promise<void> {
    // Parse propagateTraceHeaderCorsUrls from comma-separated string
    const propagateUrls = this.propagateTraceHeaderCorsUrls
      ? this.propagateTraceHeaderCorsUrls.split(',').map(url => url.trim())
      : [];

    const config: ComponentConfig = {
      debug: this.debug,
      alignmentTolerance: this.alignmentTolerance,
      maskSize: this.maskSize,
      cropMargin: this.cropMargin,
      useDocumentClassification: this.useDocumentClassification,
      useDocumentDetector: this.useDocumentDetector,
      preferredCamera: this.preferredCamera,
      captureDelay: this.captureDelay,
      telemetryCollectorUrl: this.telemetryCollectorUrl,
      metricsCollectorUrl: this.metricsCollectorUrl,
      enableTelemetry: this.enableTelemetry,
      enableMetrics: this.enableMetrics,
      customerId: this.customerId,
      tenantId: this.tenantId,
      environment: this.environment,
      propagateTraceHeaderCorsUrls: propagateUrls,
      metricsExportIntervalMillis: this.metricsExportIntervalMillis
    };

    this.serviceContainer = new ServiceContainer(config);
    this.eventBus = this.serviceContainer.getEventBus();
    this.stateManager = this.serviceContainer.getStateManager();
    this.cameraService = this.serviceContainer.getCameraService();
    this.detectionService = this.serviceContainer.getDetectionService();
    this.tracingService = this.serviceContainer.getTracingService();
    this.metricsService = this.serviceContainer.getMetricsService();

    // Set the custom trace ID if provided via prop
    if (this.tracingService && this.traceId) {
      this.tracingService.setSessionTraceId(this.traceId);
    }

    // Record component initialization
    if (this.tracingService) {
      const span = this.tracingService.startSpan('component.initialize', {
        'component.debug': this.debug,
        'component.useDocumentDetector': this.useDocumentDetector,
        'component.useDocumentClassification': this.useDocumentClassification,
      });
      this.tracingService.endSpan(span);
    }
  }

  private async validateLicense(): Promise<void> {
    if (!this.license) {
      console.error('❌ No license provided for JAAK Stamps component');
      this.licenseError = 'Este componente requiere una licencia válida. Por favor, proporcione una licencia usando el atributo "license".';
      this.licenseValid = false;
      this.updateStatus('Error de licencia', 'Licencia no proporcionada', 'error');
      return;
    }

    try {
      console.log('🔑 Validating license for JAAK Stamps...');

      // Initialize license validation service
      this.licenseValidationService = new LicenseValidationService(this.licenseEnvironment);

      // Validate the license, passing traceId and appId if provided
      const response = await this.licenseValidationService.validateLicense(this.license, this.traceId, this.appId);

      if (response && response.access_token) {
        this.licenseValid = true;
        this.licenseError = null;
        console.log('✅ License validated successfully for JAAK Stamps');

        // Emit the traceId (either provided or generated)
        if (response.traceId) {
          this.traceIdGenerated.emit({ traceId: response.traceId });
          console.log('📋 TraceId:', response.traceId);
        }
      } else {
        this.licenseValid = false;
        this.licenseError = 'La licencia proporcionada no es válida. Por favor, verifique su licencia e intente nuevamente.';
        this.updateStatus('Error de licencia', 'Licencia inválida', 'error');
        console.error('❌ Invalid license for JAAK Stamps');
      }
    } catch (error) {
      this.licenseValid = false;
      const licenseError = error as LicenseValidationError;
      this.licenseError = licenseError.error_description || 'Error al validar la licencia. Por favor, verifique su conexión e intente nuevamente.';
      this.updateStatus('Error de licencia', 'Fallo en la validación', 'error');
      console.error('❌ License validation failed for JAAK Stamps:', error);
    }
  }

  private async setupEventListeners(): Promise<void> {
    this.eventBus.on('state-changed', () => {
      this.handleStateChange();
    });

  }

  private async initializeComponent(): Promise<void> {

    this.validateProps();

    if (this.debug) {
      this.stateManager.updateCaptureState({ isLoading: true });
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    this.updateStatus('Detectando cámaras...', 'Buscando dispositivos de captura', 'initializing');
    await this.cameraService.detectDeviceType();
    await this.cameraService.enumerateDevices();
    await this.updateCameraInfoWithAutofocus();

    this.updateStatus('Preparando reconocimiento...', 'Configurando detección de documentos', 'initializing');
    await this.loadOnnxRuntime();
    this.initializeResizeObserver();
  }

  private validateProps(): void {
    if (this.maskSize < 50 || this.maskSize > 100) {
      this.maskSize = 90;
    }

    if (this.cropMargin < 0 || this.cropMargin > 100) {
      this.cropMargin = 0;
    }

    const validOptions = ['auto', 'front', 'back'];
    if (!validOptions.includes(this.preferredCamera)) {
      this.preferredCamera = 'auto';
    }

    if (this.captureDelay < 0 || this.captureDelay > 10000) {
      this.captureDelay = 1500;
    }

  }

  // Pin onnxruntime-web to a specific version so JS bindings and WASM binary
  // always come from the same release. Without a version pin, jsDelivr serves
  // "latest" and a browser that cached files from a previous release may end up
  // mixing old JS with newer WASM (or vice versa) → ABI mismatch → main-thread
  // freeze on InferenceSession.create. Bump this constant deliberately when
  // upgrading and validate against all supported browsers.
  private readonly ORT_VERSION = '1.24.3';
  private readonly ORT_CDN_BASE = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${this.ORT_VERSION}/dist/`;

  private async loadOnnxRuntime(): Promise<void> {
    if (!(window as any).ort) {
      this.updateStatus('Preparando herramientas...', 'Configurando funciones de captura', 'initializing');
      const script = document.createElement('script');

      // iOS devices use WASM-only build to prevent memory issues (WebKit 26+ bug)
      // All iOS browsers use WebKit, so this applies to Safari, Chrome, Firefox, etc.
      const ua = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(ua);

      script.src = `${this.ORT_CDN_BASE}${isIOS ? 'ort.wasm.min.js' : 'ort.min.js'}`;
      document.head.appendChild(script);

      await new Promise((resolve) => {
        script.onload = () => {
          const ort = (window as any).ort;

          // Pin the WASM binary URLs to the same release as the JS bindings.
          // Without this, ort would resolve wasm files from "latest" again.
          if (ort?.env?.wasm) {
            ort.env.wasm.wasmPaths = this.ORT_CDN_BASE;
          }

          // Configure ONNX for iOS low memory mode
          if (isIOS && ort?.env) {
            ort.env.wasm.simd = false;
            ort.env.wasm.numThreads = 1;
            ort.env.wasm.proxy = false;
          }

          setTimeout(async () => {
            await this.finalizeInitialization();
            resolve(undefined);
          }, 300);
        };
      });
    } else {
      setTimeout(async () => {
        await this.finalizeInitialization();
      }, 300);
    }
  }

  private async finalizeInitialization(): Promise<void> {
    this.stateManager.updateCaptureState({ isLoading: false });

    // Check camera permissions before showing "ready" status
    const hasPermissions = await this.checkCameraPermissions();

    if (hasPermissions) {
      this.currentStatus = {
        message: 'Listo para capturar',
        description: '',
        type: 'ready',
        isInitialized: true
      };
    } else {
      this.currentStatus = {
        message: 'Permisos de cámara requeridos',
        description: 'Por favor, otorgue permisos de cámara para continuar',
        type: 'error',
        isInitialized: true
      };
    }

    this.emitReadyEvent();
  }

  private updateStatus(message: string, description?: string, type: 'initializing' | 'ready' | 'loading' | 'active' | 'error' = 'loading'): void {
    this.currentStatus = {
      message,
      description,
      type,
      isInitialized: this.currentStatus.isInitialized
    };
  }

  private emitReadyEvent(): void {
    const isDocumentReady = !!(window as any).ort && this.detectionService.isModelLoaded();
    this.isReady.emit(isDocumentReady);
  }

  private async checkCameraPermissions(): Promise<boolean> {
    try {
      if (!navigator.permissions) {
        // Fallback: try to access camera directly
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(track => track.stop());
          return true;
        } catch {
          return false;
        }
      }

      const permission = await navigator.permissions.query({ name: 'camera' as PermissionName });
      return permission.state === 'granted';
    } catch (error) {
      return false;
    }
  }

  private handleStateChange(): void {
    // Handle state changes from StateManager if needed
  }

  private initializeResizeObserver(): void {
    if ('ResizeObserver' in window && this.detectionContainer) {
      const resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });
      resizeObserver.observe(this.detectionContainer.parentElement!);
    }

    // Handle mobile device orientation changes
    window.addEventListener('orientationchange', () => {
      // Clear any existing orientation timer
      if (this.orientationTimer) {
        clearTimeout(this.orientationTimer);
      }
      // Add delay to ensure video dimensions are updated after rotation
      this.orientationTimer = window.setTimeout(() => {
        this.handleResize();
        this.orientationTimer = undefined;
      }, 300);
    });
  }

  private handleResize(): void {
    if (this.detectionContainer) {
      const container = this.detectionContainer.parentElement!;
      const rect = container.getBoundingClientRect();
      this.updateMaskDimensions(rect);
    }
  }

  private triggerRerender(): void {
    this.captureStateVersion = this.captureStateVersion + 1;
  }

  private getGuideText(step: 'front' | 'back' | 'completed'): string {
    if (step === 'completed') {
      return 'Proceso completado';
    }

    // Check if we should show performance message
    if (this.showPerformanceMessage && step === 'front') {
      return 'Modo manual activado por rendimiento. Use el botón para capturar.';
    }

    if (step === 'front') {
      // Para el frente, si el detector manual está activo, mostrar instrucciones de captura manual
      if (this.showManualCaptureButton) {
        return 'Posicione el frente de su documento en el marco y presione el botón para capturar';
      }
      // Instrucción básica de alineación
      return 'Alinee su identificación con el marco';
    } else if (step === 'back') {
      // Para el reverso, mostrar instrucciones específicas según el modo
      if (this.useDocumentDetector) {
        return 'Si tu documento no tiene lado trasero, da clic en el botón para continuar con el proceso';
      } else {
        return 'Posicione el reverso de su documento en el marco y capture, o salte este paso';
      }
    }

    return 'Alinee su identificación con el marco';
  }

  private updateMaskDimensions(containerRect: DOMRect): void {
    if (!this.videoRef) return;

    const videoWidth = this.videoRef.videoWidth;
    const videoHeight = this.videoRef.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) return;

    // Calculate video aspect ratio and container aspect ratio
    const videoAspectRatio = videoWidth / videoHeight;
    const containerAspectRatio = containerRect.width / containerRect.height;

    // Determine how video fits in container (letterboxed or pillarboxed)
    let displayedVideoWidth: number, displayedVideoHeight: number;
    let videoOffsetX = 0, videoOffsetY = 0;

    if (videoAspectRatio > containerAspectRatio) {
      displayedVideoWidth = containerRect.width;
      displayedVideoHeight = containerRect.width / videoAspectRatio;
      videoOffsetY = (containerRect.height - displayedVideoHeight) / 2;
    } else {
      displayedVideoHeight = containerRect.height;
      displayedVideoWidth = containerRect.height * videoAspectRatio;
      videoOffsetX = (containerRect.width - displayedVideoWidth) / 2;
    }

    // Calculate mask dimensions with ID-1 aspect ratio
    const ID1_ASPECT_RATIO = 85.60 / 53.98;
    const maxWidthByHeight = displayedVideoHeight * ID1_ASPECT_RATIO;

    let maskWidthInVideo: number, maskHeightInVideo: number;
    const sizeRatio = this.maskSize / 100;

    if (maxWidthByHeight <= displayedVideoWidth) {
      maskHeightInVideo = displayedVideoHeight * sizeRatio;
      maskWidthInVideo = maskHeightInVideo * ID1_ASPECT_RATIO;
    } else {
      maskWidthInVideo = displayedVideoWidth * sizeRatio;
      maskHeightInVideo = maskWidthInVideo / ID1_ASPECT_RATIO;
    }

    // Convert to percentages of the container
    const maskWidthPercent = (maskWidthInVideo / containerRect.width) * 100;
    const maskHeightPercent = (maskHeightInVideo / containerRect.height) * 100;

    // Calculate the center position of the displayed video area
    const videoCenterX = videoOffsetX + (displayedVideoWidth / 2);
    const videoCenterY = videoOffsetY + (displayedVideoHeight / 2);

    // Convert to percentages of the container
    const videoCenterXPercent = (videoCenterX / containerRect.width) * 100;
    const videoCenterYPercent = (videoCenterY / containerRect.height) * 100;

    // Update CSS custom properties for mask sizing and positioning
    this.el.style.setProperty('--mask-width', `${maskWidthPercent}%`);
    this.el.style.setProperty('--mask-height', `${maskHeightPercent}%`);
    this.el.style.setProperty('--mask-center-x', `${videoCenterXPercent}%`);
    this.el.style.setProperty('--mask-center-y', `${videoCenterYPercent}%`);

    this.isMaskReady = true;
  }

  // PUBLIC METHODS

  // Helper method to check if component is ready for operations
  private isComponentReady(): { ready: boolean; message?: string; status?: string } {
    if (!this.currentStatus.isInitialized) {
      return {
        ready: false,
        message: 'El componente aún se está inicializando',
        status: 'initializing'
      };
    }

    if (this.currentStatus.type === 'loading' || this.currentStatus.type === 'initializing') {
      return {
        ready: false,
        message: 'El componente está cargando recursos',
        status: 'loading'
      };
    }

    if (!this.serviceContainer || !this.stateManager || !this.cameraService || !this.detectionService) {
      return {
        ready: false,
        message: 'Los servicios del componente no están disponibles',
        status: 'error'
      };
    }

    return { ready: true };
  }

  @Method()
  async getCapturedImages(): Promise<CapturedImagesResponse> {
    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      throw new Error(readyCheck.message);
    }

    const state = this.stateManager.getCaptureState();
    if (state.step !== 'completed') {
      throw new Error('El proceso de captura no ha sido completado');
    }
    return this.stateManager.getCapturedImages();
  }

  @Method()
  async isProcessCompleted() {
    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      return false; // Return false instead of error for this method
    }

    return this.stateManager.isProcessCompleted();
  }

  @Method()
  async startCapture() {
    const span = this.tracingService?.startSpan('capture.start');
    const readyCheck = this.isComponentReady();

    if (!readyCheck.ready) {
      this.updateStatus(readyCheck.message!, 'Espere a que termine la inicialización', readyCheck.status as any);
      if (span) {
        this.tracingService?.setSpanAttribute(span, 'error', true);
        this.tracingService?.setSpanAttribute(span, 'error.message', readyCheck.message!);
        this.tracingService?.endSpan(span);
      }
      return { success: false, error: readyCheck.message };
    }

    try {
      await this.startDetection();
      if (span) {
        this.tracingService?.setSpanAttribute(span, 'success', true);
        this.tracingService?.endSpan(span);
      }
      return { success: true };
    } catch (error) {
      this.updateStatus('Error al iniciar captura', error.message, 'error');
      if (span) {
        this.tracingService?.recordException(span, error);
        this.tracingService?.endSpan(span);
      }
      return { success: false, error: error.message };
    }
  }

  @Method()
  async stopCapture() {
    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      return { success: false, error: readyCheck.message };
    }

    try {
      this.exitSession();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  @Method()
  async resetCapture() {
    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      return { success: false, error: readyCheck.message };
    }

    try {
      this.resetDetection();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  @Method()
  async skipBackCapture() {
    console.log('🟡 Botón clicked: Saltar reverso');

    // Set processing state immediately
    this.processingButton = 'skip-back';

    // Add small delay to show spinner
    await new Promise(resolve => setTimeout(resolve, 100));

    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      this.processingButton = null;
      return { success: false, error: readyCheck.message };
    }

    try {
      const captureState = this.stateManager.getCaptureState();
      const capturedImages = this.stateManager.getCapturedImages();

      if (captureState.step === 'back' && capturedImages.front.fullFrame) {
        this.completeProcess(true);
        return { success: true };
      } else {
        return { success: false, error: 'No se puede saltar el reverso en el estado actual' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      // Always clear processing state when done
      this.processingButton = null;
    }
  }

  @Method()
  async getStatus(): Promise<StatusResponse> {
    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      return {
        isVideoActive: false,
        captureStep: 'front',
        hasImages: false,
        isProcessCompleted: false,
        isModelPreloaded: false,
        componentStatus: readyCheck.status,
        componentMessage: readyCheck.message
      } as StatusResponse;
    }

    try {
      const captureState = this.stateManager.getCaptureState();
      const capturedImages = this.stateManager.getCapturedImages();

      return {
        isVideoActive: captureState.isVideoActive,
        captureStep: captureState.step,
        hasImages: !!(capturedImages.front.fullFrame || capturedImages.back.fullFrame),
        isProcessCompleted: this.stateManager.isProcessCompleted(),
        isModelPreloaded: this.detectionService.isModelLoaded(),
        componentStatus: 'ready'
      };
    } catch (error) {
      return {
        isVideoActive: false,
        captureStep: 'front',
        hasImages: false,
        isProcessCompleted: false,
        isModelPreloaded: false,
        componentStatus: 'error',
        componentMessage: error.message
      } as StatusResponse;
    }
  }

  @Method()
  async preloadModel() {
    const span = this.tracingService?.startSpan('model.preload');
    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      this.updateStatus(readyCheck.message!, 'Espere a que termine la inicialización', readyCheck.status as any);
      if (span) {
        this.tracingService?.setSpanAttribute(span, 'error', true);
        this.tracingService?.endSpan(span);
      }
      return { success: false, error: readyCheck.message };
    }

    if (this.detectionService.isModelLoaded()) {
      this.updateStatus('Reconocimiento listo', '', 'ready');
      if (span) {
        this.tracingService?.setSpanAttribute(span, 'cached', true);
        this.tracingService?.endSpan(span);
      }
      return { success: true, message: 'Model already loaded' };
    }

    try {
      const loadStartTime = performance.now();

      this.updateStatus('Preparando reconocimiento...', 'Configurando detección de documentos', 'loading');
      if (this.stateManager) {
        this.stateManager.updateCaptureState({ isLoading: true });
      }

      try {
        const detectionStartTime = performance.now();
        const modelSpan = this.tracingService?.startSpan('model.detection.load');
        await this.detectionService.loadModel();
        if (modelSpan) this.tracingService?.endSpan(modelSpan);

        const detectionLoadTime = performance.now() - detectionStartTime;
        this.metricsService?.recordModelLoad('detection', detectionLoadTime, false);
      } catch (modelError) {
        this.metricsService?.recordError('model_load_failed', 'detection');
        this.switchToManualModeWithError('Error al cargar modelo de detección');
        throw modelError;
      }

      this.updateStatus('Optimizando detección...', 'Configurando reconocimiento de documentos', 'loading');

      try {
        const classificationStartTime = performance.now();
        const classSpan = this.tracingService?.startSpan('model.classification.load');
        await this.detectionService.loadClassificationModel();
        if (classSpan) this.tracingService?.endSpan(classSpan);

        const classificationLoadTime = performance.now() - classificationStartTime;
        this.metricsService?.recordModelLoad('classification', classificationLoadTime, false);
      } catch (classificationError) {
        this.metricsService?.recordError('model_load_failed', 'classification');
        this.switchToManualModeWithError('Error al cargar modelo de clasificación');
        throw classificationError;
      }

      const loadEndTime = performance.now();
      const loadTime = loadEndTime - loadStartTime;

      // Record ONNX load time
      if (this.debug) {
        this.recordOnnxPerformance(loadTime, 0);
      }

      if (span) {
        this.tracingService?.setSpanAttribute(span, 'loadTime', loadTime);
      }

      this.updateStatus('Finalizando configuración...', 'Preparando herramientas para captura', 'loading');
      await new Promise(resolve => setTimeout(resolve, 300));

      this.updateStatus('Reconocimiento preparado', '', 'ready');
      if (this.stateManager) {
        this.stateManager.updateCaptureState({ isLoading: false });
      }
      this.emitReadyEvent();

      if (span) {
        this.tracingService?.setSpanAttribute(span, 'success', true);
        this.tracingService?.endSpan(span);
      }

      return { success: true, message: 'Models preloaded successfully' };
    } catch (error) {
      this.updateStatus('Error de configuración', 'No se pudieron preparar las herramientas necesarias', 'error');
      if (this.stateManager) {
        this.stateManager.updateCaptureState({ isLoading: false });
      }
      if (span) {
        this.tracingService?.recordException(span, error);
        this.tracingService?.endSpan(span);
      }
      return { success: false, error: error.message };
    }
  }

  @Method()
  async getCameraInfo(): Promise<CameraInfoResponse> {
    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      return {
        availableCameras: [],
        selectedCameraId: null,
        deviceType: 'desktop',
        isMultipleCamerasAvailable: false,
        preferredFacing: null,
        error: readyCheck.message
      } as CameraInfoResponse;
    }

    try {
      return await this.cameraService.getCameraInfo();
    } catch (error) {
      return {
        availableCameras: [],
        selectedCameraId: null,
        deviceType: 'desktop',
        isMultipleCamerasAvailable: false,
        preferredFacing: null,
        error: error.message
      } as CameraInfoResponse;
    }
  }

  @Method()
  async setPreferredCamera(camera: 'auto' | 'front' | 'back') {
    const readyCheck = this.isComponentReady();
    if (!readyCheck.ready) {
      return {
        success: false,
        error: readyCheck.message,
        selectedCamera: null,
        availableCameras: 0
      };
    }

    try {
      this.preferredCamera = camera;
      this.serviceContainer.updateConfig({ preferredCamera: camera });

      await this.cameraService.enumerateDevices();
      await this.updateCameraInfoWithAutofocus();

      const captureState = this.stateManager.getCaptureState();
      if (captureState.isVideoActive) {
        const selectedCameraId = this.cameraService.getSelectedCameraId();
        if (selectedCameraId) {
          await this.cameraService.switchCamera(selectedCameraId);
        }
      }

      return {
        success: true,
        selectedCamera: this.cameraService.getSelectedCameraId(),
        availableCameras: this.cameraService.getAvailableCameras().length
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        selectedCamera: null,
        availableCameras: 0
      };
    }
  }

  @Method()
  async setCaptureDelay(delay: number) {
    if (delay < 0 || delay > 10000) {
      return {
        success: false,
        error: 'Capture delay must be between 0 and 10000 milliseconds',
        captureDelay: this.captureDelay
      };
    }

    // Allow setting capture delay even during initialization
    this.captureDelay = delay;
    if (this.serviceContainer) {
      this.serviceContainer.updateConfig({ captureDelay: delay });
    }

    return {
      success: true,
      captureDelay: this.captureDelay
    };
  }

  @Method()
  async getCaptureDelay() {
    // Always allow getting capture delay, even during initialization
    return this.captureDelay;
  }





  // Memory and device capability detection
  private checkDeviceCapabilities(): { canUseML: boolean; reason: string; memoryMB: number | null } {
    const deviceMemory = (navigator as any).deviceMemory;

    // Siempre intentar usar ML, independientemente del dispositivo
    // Las restricciones anteriores no eran confiables
    return {
      canUseML: true,
      reason: 'Dispositivo compatible con detección automática.',
      memoryMB: deviceMemory ? deviceMemory * 1024 : null
    };
  }

  private debugLog(message: string, details?: unknown): void {
    if (!this.debug) {
      return;
    }

    if (typeof details === 'undefined') {
      console.log(`[jaak-stamps][diag] ${message}`);
      return;
    }

    console.log(`[jaak-stamps][diag] ${message}`, details);
  }

  private debugWarn(message: string, details?: unknown): void {
    if (!this.debug) {
      return;
    }

    if (typeof details === 'undefined') {
      console.warn(`[jaak-stamps][diag] ${message}`);
      return;
    }

    console.warn(`[jaak-stamps][diag] ${message}`, details);
  }

  private summarizeId(id?: string): string | null {
    if (!id) {
      return null;
    }

    return id.length <= 8 ? id : `${id.slice(0, 8)}...`;
  }

  private getCameraDevicesSnapshot(): Array<Record<string, unknown>> {
    return this.cameraService.getAvailableCameras().map(camera => ({
      label: camera.label || '(sin label)',
      deviceId: this.summarizeId(camera.deviceId),
      groupId: this.summarizeId(camera.groupId),
      kind: camera.kind,
    }));
  }

  private sanitizeTrackSettings(settings?: MediaTrackSettings): Record<string, unknown> | null {
    if (!settings) {
      return null;
    }

    return {
      ...settings,
      deviceId: this.summarizeId(settings.deviceId),
      groupId: this.summarizeId((settings as MediaTrackSettings & { groupId?: string }).groupId),
    };
  }

  private sanitizeTrackConstraints(track?: MediaStreamTrack): Record<string, unknown> | null {
    if (!track) {
      return null;
    }

    const constraints = track.getConstraints ? track.getConstraints() : {};
    const sanitizedConstraints: Record<string, unknown> = { ...constraints };

    if (typeof sanitizedConstraints.deviceId === 'string') {
      sanitizedConstraints.deviceId = this.summarizeId(sanitizedConstraints.deviceId);
    }

    return sanitizedConstraints;
  }

  private getStreamSnapshot(stream?: MediaStream | null): Record<string, unknown> {
    if (!stream) {
      return { hasStream: false };
    }

    const videoTrack = stream.getVideoTracks()[0];

    if (!videoTrack) {
      return {
        hasStream: true,
        streamActive: stream.active,
        trackCount: stream.getTracks().length,
        hasVideoTrack: false,
      };
    }

    return {
      hasStream: true,
      streamActive: stream.active,
      trackCount: stream.getTracks().length,
      trackLabel: videoTrack.label || '(sin label)',
      trackEnabled: videoTrack.enabled,
      trackMuted: videoTrack.muted,
      trackReadyState: videoTrack.readyState,
      settings: this.sanitizeTrackSettings(videoTrack.getSettings ? videoTrack.getSettings() : undefined),
      constraints: this.sanitizeTrackConstraints(videoTrack),
    };
  }

  private getVideoElementSnapshot(): Record<string, unknown> {
    if (!this.videoRef) {
      return { hasVideoRef: false };
    }

    const computedStyle = this.el.ownerDocument?.defaultView?.getComputedStyle(this.videoRef);

    return {
      hasVideoRef: true,
      isConnected: this.videoRef.isConnected,
      readyState: this.videoRef.readyState,
      networkState: this.videoRef.networkState,
      paused: this.videoRef.paused,
      muted: this.videoRef.muted,
      autoplay: this.videoRef.autoplay,
      playsInline: this.videoRef.playsInline,
      currentTime: Number.isFinite(this.videoRef.currentTime)
        ? Number(this.videoRef.currentTime.toFixed(3))
        : this.videoRef.currentTime,
      videoWidth: this.videoRef.videoWidth,
      videoHeight: this.videoRef.videoHeight,
      clientWidth: this.videoRef.clientWidth,
      clientHeight: this.videoRef.clientHeight,
      hasSrcObject: !!this.videoRef.srcObject,
      display: computedStyle?.display ?? 'unknown',
      visibility: computedStyle?.visibility ?? 'unknown',
    };
  }

  private attachVideoDiagnosticListeners(): () => void {
    if (!this.debug || !this.videoRef) {
      return () => undefined;
    }

    const video = this.videoRef;
    const listeners = this.VIDEO_DIAGNOSTIC_EVENTS.map(eventName => {
      const handler = () => {
        this.debugLog(`video event "${eventName}"`, this.getVideoElementSnapshot());
      };
      video.addEventListener(eventName, handler);
      return { eventName, handler };
    });

    return () => {
      listeners.forEach(({ eventName, handler }) => {
        video.removeEventListener(eventName, handler);
      });
    };
  }

  // DETECTION METHODS
  private async startDetection(): Promise<void> {
    try {
      if (this.debug) {
        console.log('[JAAK-DEBUG] startDetection() called');
        console.log('[JAAK-DEBUG] useDocumentDetector:', this.useDocumentDetector);
        console.log('[JAAK-DEBUG] useDocumentClassification:', this.useDocumentClassification);
      }

      // Verificar capacidades del dispositivo antes de cargar modelos
      const capabilities = this.checkDeviceCapabilities();

      if (this.debug) {
        console.log('[JAAK-DEBUG] Device capabilities:', JSON.stringify(capabilities));
      }

      // Si el dispositivo no puede usar ML o tenemos problemas de memoria, usar modo manual
      if (!capabilities.canUseML && this.useDocumentDetector) {
        if (this.debug) {
          console.warn('[JAAK-DEBUG] MANUAL MODE: Device cannot use ML. Reason:', capabilities.reason);
        }
        this.switchToManualModeWithWarning(capabilities.reason);
        return;
      }

      // Paso 1: Verificar y cargar modelos si es necesario
      if (!this.detectionService.isModelLoaded()) {
        const loadStartTime = performance.now();

        if (this.debug) {
          console.log('[JAAK-DEBUG] Loading detection model...');
        }

        this.updateStatus('Preparando reconocimiento...', 'Configurando detección de documentos', 'loading');
        this.stateManager.updateCaptureState({ isLoading: true });

        try {
          await this.detectionService.loadModel();
          if (this.debug) {
            console.log('[JAAK-DEBUG] Detection model loaded successfully in', (performance.now() - loadStartTime).toFixed(0), 'ms');
          }
        } catch (modelError) {
          // Si es un error de memoria, cambiar a modo manual con mensaje específico
          if (modelError.message.includes('Out of memory') || modelError.message.includes('no available backend')) {
            const memoryInfo = (navigator as any).deviceMemory ? `(${(navigator as any).deviceMemory}GB disponible)` : '';
            if (this.debug) {
              console.warn('[JAAK-DEBUG] MANUAL MODE: Out of memory loading detection model.', memoryInfo);
            }
            this.switchToManualModeWithWarning(`Memoria insuficiente para detección automática ${memoryInfo}. Modo manual activado - funciona igual de bien!`);
            return;
          } else {
            if (this.debug) {
              console.error('[JAAK-DEBUG] MANUAL MODE: Error loading detection model:', modelError.message);
            }
            this.switchToManualModeWithError('Error al cargar modelo de detección');
            throw modelError;
          }
        }

        this.updateStatus('Optimizando detección...', 'Configurando reconocimiento de documentos', 'loading');

        if (this.debug) {
          console.log('[JAAK-DEBUG] Loading classification model...');
        }

        try {
          await this.detectionService.loadClassificationModel();
          if (this.debug) {
            console.log('[JAAK-DEBUG] Classification model loaded successfully');
          }
        } catch (classificationError) {
          // La clasificacion es opcional, no debe bloquear la deteccion
          if (this.debug) {
            console.warn('[JAAK-DEBUG] Classification model failed to load, disabling classification. Error:', classificationError.message);
          }
          this.classificationDisabled = true;
        }

        const loadEndTime = performance.now();
        const loadTime = loadEndTime - loadStartTime;

        if (this.debug) {
          console.log('[JAAK-DEBUG] All models loaded in', loadTime.toFixed(0), 'ms');
          this.recordOnnxPerformance(loadTime, 0);
        }

      } else {
        if (this.debug) {
          console.log('[JAAK-DEBUG] Models already loaded, skipping load');
        }
      }

      // Paso 2: Detectar y configurar dispositivos
      this.updateStatus('Configurando cámara...', 'Buscando dispositivos disponibles', 'loading');

      try {
        await this.cameraService.enumerateDevices();
        this.debugLog('step 2/5 enumerateDevices()', {
          cameras: this.getCameraDevicesSnapshot(),
          selectedCameraId: this.summarizeId(this.cameraService.getSelectedCameraId() || undefined),
        });
      } catch (enumerateError) {
        throw new Error(`Error al enumerar dispositivos: ${enumerateError.message}`);
      }

      try {
        await this.updateCameraInfoWithAutofocus();
        this.debugLog('step 2/5 updateCameraInfoWithAutofocus()', {
          cameraInfo: this.cameraInfoWithAutofocus,
        });
      } catch (cameraInfoError) {
        throw new Error(`Error al actualizar información de cámara: ${cameraInfoError.message}`);
      }

      // Paso 3: Configurar cámara seleccionada
      this.updateStatus('Ajustando cámara...', 'Configurando calidad de imagen', 'loading');

      let stream: MediaStream;
      try {
        stream = await this.cameraService.setupCamera();
        this.debugLog('step 3/5 setupCamera() resolved', {
          stream: this.getStreamSnapshot(stream),
          videoRef: this.getVideoElementSnapshot(),
        });
      } catch (setupError) {
        throw new Error(`Error al configurar cámara: ${setupError.message}`);
      }

      // Paso 4: Inicializar video y mostrar estado de análisis inicial
      if (this.useDocumentDetector) {
        this.updateStatus('Analizando estabilidad...', 'Evaluando rendimiento del sistema', 'loading');
      } else {
        this.updateStatus('Captura activa', 'Posicione su documento y use el botón para capturar', 'active');
      }

      try {
        this.debugLog('step 4/5 initializeVideoStream() start', {
          stream: this.getStreamSnapshot(stream),
          videoRef: this.getVideoElementSnapshot(),
        });
        await this.initializeVideoStream(stream);
        this.debugLog('step 4/5 initializeVideoStream() completed', {
          stream: this.getStreamSnapshot(stream),
          videoRef: this.getVideoElementSnapshot(),
        });
      } catch (videoError) {
        throw new Error(`Error al inicializar video: ${videoError.message}`);
      }

      // Paso 5: Calibrar máscara
      if (this.detectionContainer) {
        const container = this.detectionContainer.parentElement!;
        const rect = container.getBoundingClientRect();
        this.updateMaskDimensions(rect);
      }


      // Finalizar e iniciar captura
      this.startTime = Date.now();
      this.stateManager.updateCaptureState({ isLoading: false });

      // Mostrar botón manual si el detector está deshabilitado
      if (!this.useDocumentDetector) {
        this.showManualCaptureButton = true;
      }

      this.debugLog('step 5/5 capture loop ready', {
        videoRef: this.getVideoElementSnapshot(),
        stream: this.getStreamSnapshot(this.videoStream),
        useDocumentDetector: this.useDocumentDetector,
      });

      this.detectFrame();

    } catch (err) {
      this.debugWarn('startDetection() failed', {
        error: err instanceof Error ? err.message : String(err),
        videoRef: this.getVideoElementSnapshot(),
        stream: this.getStreamSnapshot(this.videoStream),
      });
      console.error('[jaak-stamps] Error al preparar captura:', err);
      this.updateStatus('Error al iniciar', 'No se pudo preparar la captura', 'error');
      this.stateManager.updateCaptureState({ isLoading: false });
    }
  }

  private async initializeVideoStream(stream: MediaStream): Promise<void> {
    if (!this.videoRef) {
      throw new Error('Video element not available');
    }

    const detachVideoDiagnosticListeners = this.attachVideoDiagnosticListeners();

    this.debugLog('initializeVideoStream() pre-assign', {
      stream: this.getStreamSnapshot(stream),
      videoRef: this.getVideoElementSnapshot(),
    });

    this.videoRef.srcObject = stream;
    this.videoStream = stream;

    this.debugLog('initializeVideoStream() post-srcObject assignment', {
      stream: this.getStreamSnapshot(stream),
      videoRef: this.getVideoElementSnapshot(),
    });

    const isRear = this.cameraService.isRearCamera(stream);
    this.shouldMirrorVideo = !isRear;

    const LOAD_METADATA_TIMEOUT_MS = 10000;

    // Diagnostic instrumentation: when the timeout fires we need enough
    // context to distinguish between "OS muted the track", "permission
    // revoked mid-flight", "device never produced frames" and other modes
    // that all surface as the same generic timeout error.
    const track = stream.getVideoTracks()[0];
    const waitStart = performance.now();
    const trackEvents: Array<{ at: number; event: string; state?: string }> = [];
    const recordTrackEvent = (event: string) => {
      trackEvents.push({
        at: Math.round(performance.now() - waitStart),
        event,
        state: track?.readyState,
      });
    };
    const onTrackMute = () => recordTrackEvent('mute');
    const onTrackUnmute = () => recordTrackEvent('unmute');
    const onTrackEnded = () => recordTrackEvent('ended');
    if (track) {
      track.addEventListener('mute', onTrackMute);
      track.addEventListener('unmute', onTrackUnmute);
      track.addEventListener('ended', onTrackEnded);
    }
    const cleanupTrackListeners = () => {
      if (!track) return;
      track.removeEventListener('mute', onTrackMute);
      track.removeEventListener('unmute', onTrackUnmute);
      track.removeEventListener('ended', onTrackEnded);
    };
    const captureDiagnostics = () => {
      const settings = track?.getSettings?.();
      const computed = this.videoRef ? getComputedStyle(this.videoRef) : null;
      return {
        reason: 'VIDEO_METADATA_TIMEOUT',
        waitedMs: Math.round(performance.now() - waitStart),
        stream: {
          videoTrackCount: stream.getVideoTracks().length,
          audioTrackCount: stream.getAudioTracks().length,
        },
        track: track ? {
          readyState: track.readyState,
          muted: track.muted,
          enabled: track.enabled,
          label: track.label,
          kind: track.kind,
          settings: settings ? {
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
            deviceId: settings.deviceId,
            facingMode: settings.facingMode,
          } : null,
        } : null,
        video: this.videoRef ? {
          readyState: this.videoRef.readyState,
          networkState: this.videoRef.networkState,
          error: this.videoRef.error ? { code: this.videoRef.error.code, message: this.videoRef.error.message } : null,
          paused: this.videoRef.paused,
          offsetWidth: this.videoRef.offsetWidth,
          offsetHeight: this.videoRef.offsetHeight,
          videoWidth: this.videoRef.videoWidth,
          videoHeight: this.videoRef.videoHeight,
          display: computed?.display,
          visibility: computed?.visibility,
        } : null,
        document: {
          visibilityState: document.visibilityState,
          hasFocus: document.hasFocus(),
        },
        trackEvents,
        userAgent: navigator.userAgent,
      };
    };

    const finalizeStreamInitialization = async (): Promise<void> => {
      try {
        await this.videoRef!.play();
      } catch (playError) {
        console.error('[jaak-stamps] video.play() failed (likely autoplay policy):', playError);
        throw playError;
      }

      this.stateManager.updateCaptureState({ isVideoActive: true });

      if (this.detectionContainer) {
        const container = this.detectionContainer.parentElement!;
        const rect = container.getBoundingClientRect();
        this.updateMaskDimensions(rect);
      }

      this.debugLog('initializeVideoStream() finalized after play()', {
        stream: this.getStreamSnapshot(stream),
        videoRef: this.getVideoElementSnapshot(),
      });
    };

    // If metadata is already available (readyState >= HAVE_METADATA = 1),
    // the 'loadedmetadata' event will not fire again. Handle both cases.
    if (this.videoRef.readyState >= 1) {
      this.debugLog('video metadata already available, skipping event wait', this.getVideoElementSnapshot());
      cleanupTrackListeners();
      await finalizeStreamInitialization();
      detachVideoDiagnosticListeners();
      return;
    }

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        this.videoRef!.onloadedmetadata = null;
        this.videoRef!.onerror = null;
        detachVideoDiagnosticListeners();
      };

      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        const diagnostics = captureDiagnostics();
        this.debugWarn(`initializeVideoStream() timeout after ${LOAD_METADATA_TIMEOUT_MS}ms`, {
          videoRef: this.getVideoElementSnapshot(),
          stream: this.getStreamSnapshot(stream),
        });
        console.error('[jaak-stamps] Video metadata timeout diagnostics (json):', JSON.stringify(diagnostics));
        cleanup();
        cleanupTrackListeners();
        reject(new Error(`Video metadata load timeout after ${LOAD_METADATA_TIMEOUT_MS}ms`));
      }, LOAD_METADATA_TIMEOUT_MS);

      this.videoRef!.onloadedmetadata = async () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        this.debugLog('initializeVideoStream() onloadedmetadata fired', {
          videoRef: this.getVideoElementSnapshot(),
          stream: this.getStreamSnapshot(stream),
        });
        cleanupTrackListeners();
        try {
          await finalizeStreamInitialization();
          cleanup();
          resolve();
        } catch (err) {
          cleanup();
          reject(err);
        }
      };

      this.videoRef!.onerror = (event) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        this.debugWarn('initializeVideoStream() video element emitted error', {
          event,
          videoRef: this.getVideoElementSnapshot(),
          stream: this.getStreamSnapshot(stream),
        });
        cleanup();
        cleanupTrackListeners();
        reject(new Error(`Video element error: ${event}`));
      };
    });
  }


  private async detectFrame(): Promise<void> {
    try {
      const frameStartTime = performance.now();
      const captureState = this.stateManager.getCaptureState();
      if (!this.videoRef || !this.detectionContainer || !this.detectionService.isModelLoaded()) {
        if (this.debug) {
          console.log('[JAAK-DEBUG] detectFrame() skipped: videoRef=', !!this.videoRef, 'detectionContainer=', !!this.detectionContainer, 'modelLoaded=', this.detectionService.isModelLoaded());
        }
        return;
      }


      // Show manual capture button when document detector is disabled or performance is degraded
      if (!this.useDocumentDetector || this.performanceDegradedMode) {
        if (this.debug && !this._manualModeLoggedOnce) {
          console.warn('[JAAK-DEBUG] detectFrame() in MANUAL MODE: useDocumentDetector=', this.useDocumentDetector, 'performanceDegradedMode=', this.performanceDegradedMode);
          this._manualModeLoggedOnce = true;
        }
        this.showManualCaptureButton = true;
        // Continue the loop for UI updates but skip detection logic
        if (captureState.step !== 'completed') {
          this.animationId = requestAnimationFrame(() => this.detectFrame());
        }
        return;
      } else if (!this.performanceDegradedMode) {
        this.showManualCaptureButton = false;
      }

      if (captureState.isDetectionPaused) {
        if (captureState.step !== 'completed') {
          this.animationId = requestAnimationFrame(() => this.detectFrame());
        }
        return;
      }

      // Adaptive frame skipping for performance
      this.frameSkipCounter++;
      const currentFrameSkip = this.getAdaptiveFrameSkip();
      if (this.frameSkipCounter <= currentFrameSkip) {
        if (captureState.step !== 'completed') {
          this.animationId = requestAnimationFrame(() => this.detectFrame());
        }
        return;
      }
      this.frameSkipCounter = 0;

      // Throttle inference - use higher interval for mobile devices to prevent slow motion effect
      const currentTime = Date.now();
      const isMobileDevice = this.cameraInfoWithAutofocus.deviceType === 'mobile' ||
                             /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isTablet = this.cameraInfoWithAutofocus.deviceType === 'tablet' ||
                       (/iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768);
      const minInterval = (isMobileDevice && !isTablet) ? this.MOBILE_MIN_INFERENCE_INTERVAL : this.MIN_INFERENCE_INTERVAL;

      if (currentTime - this.lastInferenceTime < minInterval) {
        if (captureState.step !== 'completed') {
          this.animationId = requestAnimationFrame(() => this.detectFrame());
        }
        return;
      }
      this.lastInferenceTime = currentTime;

      // Measure preprocessing and inference time
      const inferenceStartTime = performance.now();

      let inputTensor;
      let detections;
      let inferenceTime;

      try {
        inputTensor = this.detectionService.preprocess(this.videoRef);
      } catch (preprocessError) {
        if (this.debug) {
          console.error('[JAAK-DEBUG] MANUAL MODE: Preprocess error:', preprocessError.message);
        }
        this.switchToManualModeWithError('Error al procesar imagen');
        return;
      }

      try {
        // Standard detection without timeout (timeout is handled by frame counting)
        detections = await this.detectionService.runInference(inputTensor);
        inferenceTime = performance.now() - inferenceStartTime;
      } catch (inferenceError) {
        if (this.debug) {
          console.error('[JAAK-DEBUG] MANUAL MODE: Inference error:', inferenceError.message);
        }
        this.switchToManualModeWithError('Error en detección automática');
        return;
      }

      // Increment frame count
      this.processedFramesCount++;


      // Record ONNX performance metrics
      if (this.debug) {
        this.recordOnnxPerformance(0, inferenceTime);
      }

      // Update best score logic
      if (this.startTime && Date.now() - this.startTime < 5000) {
        detections.forEach((detection: DetectionBox) => {
          const isWellPositioned = this.detectionService.isCardInFrame(detection);
          const effectiveScore = isWellPositioned ? detection.score * 1.2 : detection.score;

          if (effectiveScore > captureState.bestScore) {
            this.stateManager.updateCaptureState({ bestScore: effectiveScore });
          }
        });
      }

      // Update document detection state
      const wasDocumentDetected = this.hasDocumentDetected;
      this.hasDocumentDetected = detections.length > 0;

      // Restart timer if document detected during back capture
      if (!wasDocumentDetected && this.hasDocumentDetected &&
          captureState.step === 'back' && this.enableBackDocumentTimer) {
        this.startBackDocumentTimer();
      }

      // Adaptive frame rate
      if (detections.length === 0) {
        this.consecutiveFailures++;
      } else {
        this.consecutiveFailures = 0;
      }

      // Update detection boxes for debug
      if (this.debug) {
        this.updateDetectionBoxes(detections);
      } else {
        this.detectionBoxes = [];
      }

      this.updateMaskColor(detections);

      // Record frame processing metrics
      const frameEndTime = performance.now();
      const totalFrameTime = frameEndTime - frameStartTime;

      // Sistema simplificado: Verificar rendimiento de cada frame (solo si no se ha cambiado a manual)
      if (this.useDocumentDetector && !this.hasAutoSwitchedToManual) {
        if (this.debug) {
          console.log(`[JAAK-DEBUG] Frame #${this.processedFramesCount} time: ${totalFrameTime.toFixed(0)}ms (threshold: ${this.PERFORMANCE_THRESHOLD_MS}ms) | inference: ${inferenceTime.toFixed(0)}ms | slowFrames: ${this.slowFrameCount}/${this.SLOW_FRAMES_TO_TRIGGER}`);
        }
        // Verificar si el frame es lento
        if (totalFrameTime >= this.PERFORMANCE_THRESHOLD_MS) {
          this.slowFrameCount++;

          if (this.debug) {
            console.warn(`[JAAK-DEBUG] SLOW FRAME detected: ${totalFrameTime.toFixed(0)}ms >= ${this.PERFORMANCE_THRESHOLD_MS}ms | slowFrameCount: ${this.slowFrameCount}/${this.SLOW_FRAMES_TO_TRIGGER}`);
          }

          // Si tenemos suficientes frames lentos consecutivos, cambiar a manual
          if (this.slowFrameCount >= this.SLOW_FRAMES_TO_TRIGGER) {
            if (this.debug) {
              console.error(`[JAAK-DEBUG] MANUAL MODE: Performance degraded. ${this.slowFrameCount} slow frames (>= ${this.PERFORMANCE_THRESHOLD_MS}ms) exceeded threshold of ${this.SLOW_FRAMES_TO_TRIGGER}`);
            }
            this.switchToManualMode();
          }
        } else {
          // Frame rápido, reiniciar contador
          if (this.slowFrameCount > 0) {
            if (this.debug) {
              console.log(`[JAAK-DEBUG] Fast frame, resetting slowFrameCount from ${this.slowFrameCount} to 0`);
            }
            this.slowFrameCount = 0;
          }
        }

        // Cambiar mensaje de estado después de analizar los primeros 2 frames
        if (this.processedFramesCount === 2) {
          this.updateStatus('Captura activa', 'Buscando documento en el marco de captura', 'active');
        }
      }

      if (this.debug) {
        this.recordFrameProcessing(totalFrameTime, detections.length);
      }

      // Continue detection loop
      if (captureState.step !== 'completed') {
        if (this.consecutiveFailures > this.MAX_FAILURES) {
          setTimeout(() => this.detectFrame(), 200);
        } else {
          this.animationId = requestAnimationFrame(() => this.detectFrame());
        }
      }
    } catch (e) {
      const captureState = this.stateManager.getCaptureState();
      if (captureState.step !== 'completed') {
        setTimeout(() => this.detectFrame(), 100);
      }
    }
  }

  // ... (continuing with remaining methods)
  // Due to length constraints, I'll provide the key architectural changes

  disconnectedCallback() {
    this.cleanup();
  }

  private cleanup(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
    }
    if (this.alignmentTimer) {
      clearTimeout(this.alignmentTimer);
      this.alignmentTimer = undefined;
    }

    if (this.orientationTimer) {
      clearTimeout(this.orientationTimer);
      this.orientationTimer = undefined;
    }

    // Liberar recursos de ONNX al limpiar el componente
    this.releaseOnnxResources();
    if (this.performanceUpdateInterval) {
      clearInterval(this.performanceUpdateInterval);
      this.performanceUpdateInterval = undefined;
    }

    // Clear canvas pool
    this.canvasPool = [];

    this.detectionBoxes = [];
    this.alignmentStartTime = undefined;
    this.hasDocumentDetected = false;
    this.serviceContainer?.cleanup();
  }

  render() {
    const captureState = this.stateManager?.getCaptureState() || {
      isVideoActive: false,
      isLoading: false,
      showFlipAnimation: false,
      showSuccessAnimation: false,
      step: 'front',
      isCapturing: false
    };
    const cameraInfo = this.cameraInfoWithAutofocus;

    return (
      <div class="detector-container">
        {/* License validation error message */}
        {!this.licenseValid && this.licenseError && (
          <div class="license-error-container">
            <div class="license-error-card">
              <svg class="license-error-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 8V12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="16" r="0.5" fill="currentColor" stroke="currentColor" stroke-width="1"/>
              </svg>
              <h2 class="license-error-title">Licencia Requerida</h2>
              <p class="license-error-message">{this.licenseError}</p>
              <p class="license-error-help">
                Contacte a soporte: <a href="mailto:support@jaak.ai">support@jaak.ai</a>
              </p>
              <div class="license-error-footer">JAAK Stamps</div>
            </div>
          </div>
        )}

        {/* Component content loads only if license is valid */}
        {this.licenseValid && (
          <div class="video-container">
          <video
            ref={el => this.videoRef = el}
            autoplay
            muted
            playsinline
            class={this.shouldMirrorVideo ? 'mirror' : ''}
            style={{
              // Keep the element rendered (display: block) so the browser
              // loads stream metadata and fires 'loadedmetadata'. Hiding it
              // with display:none prevents metadata loading in Chrome and
              // causes initializeVideoStream to hang waiting for the event.
              // Use opacity + visibility to hide visually while keeping the
              // render context alive.
              opacity: captureState.isVideoActive ? '1' : '0',
              visibility: captureState.isVideoActive ? 'visible' : 'hidden',
            }}
          />
          <div
            ref={el => this.detectionContainer = el}
            class={`detection-overlay ${this.shouldMirrorVideo ? 'mirror' : ''}`}
          >
            {this.debug && this.detectionBoxes.map((box, index) => (
              <div
                key={index}
                class="detection-box"
                style={{
                  position: 'absolute',
                  left: `${box.x}px`,
                  top: `${box.y}px`,
                  width: `${box.w}px`,
                  height: `${box.h}px`,
                  border: '2px solid #32406C',
                  pointerEvents: 'none',
                  boxSizing: 'border-box'
                }}
              />
            ))}
          </div>

          {this.isMaskReady && (
            <div class="overlay-mask">
              <div class="card-outline">
                <div class="side side-top"></div>
                <div class="side side-right"></div>
                <div class="side side-bottom"></div>
                <div class="side side-left"></div>
              </div>

              {/* Texto guía fuera del marco del documento */}
              {!captureState.showFlipAnimation && !captureState.showSuccessAnimation && (
                <div class={`guide-text ${this.showPerformanceMessage ? 'performance-warning-text' : ''}`}>
                  {this.getGuideText(captureState.step)}
                </div>
              )}

              {captureState.step === 'back' && !captureState.showFlipAnimation && !captureState.showSuccessAnimation && (
                <div class="back-capture-section">
                  <div class="back-capture-buttons">
                    {(!this.useDocumentDetector || this.performanceDegradedMode || this.showManualCaptureButton) && (
                      <button
                        class="capture-button primary-button"
                        onClick={() => this.takeManualScreenshot()}
                        type="button"
                        disabled={this.processingButton !== null}
                      >
                        {this.processingButton === 'capture-back' && (
                          <span class="button-spinner"></span>
                        )}
                        Capturar Reverso
                      </button>
                    )}
                    <button
                      class="skip-button"
                      onClick={() => this.skipBackCapture()}
                      type="button"
                      disabled={this.processingButton !== null}
                    >
                      {this.processingButton === 'skip-back' && (
                        <span class="button-spinner"></span>
                      )}
                      {this.enableBackDocumentTimer && this.backDocumentTimerRemaining > 0
                        ? `Saltar reverso (${this.backDocumentTimerRemaining}s)`
                        : 'Saltar reverso'
                      }
                    </button>
                  </div>
                </div>
              )}

              {/* Camera controls */}
              {captureState.isVideoActive && (
                <div class="camera-controls">

                  <button
                    class={`camera-selector-button ${this.isSwitchingCamera ? 'loading' : ''}`}
                    onClick={() => this.toggleCameraSelector()}
                    type="button"
                    title="Seleccionar cámara"
                    disabled={this.isSwitchingCamera}
                  >
                    {this.isSwitchingCamera ? (
                      <div class="button-spinner"></div>
                    ) : (
                      'Cámaras'
                    )}
                  </button>
                </div>
              )}

              {/* Camera selector dropdown */}
              {this.showCameraSelector && cameraInfo.availableCameras.length > 0 && (
                <div class="camera-selector-dropdown">
                  <div class="camera-selector-header">
                    <span>Seleccionar Cámara</span>
                    <button
                      class="close-selector"
                      onClick={() => this.toggleCameraSelector()}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                  <div class="camera-list">
                    {cameraInfo.availableCameras.map((camera) => (
                      <button
                        key={camera.id}
                        class={`camera-option ${cameraInfo.selectedCameraId === camera.id ? 'selected' : ''}`}
                        onClick={() => this.handleCameraSwitch(camera.id)}
                        type="button"
                      >
                        <span class="camera-label">
                          {camera.label || `Cámara ${cameraInfo.availableCameras.indexOf(camera) + 1}`}
                          {camera.hasAutofocus && (
                            <span class="autofocus-icon" title="Autofoco disponible">⦿</span>
                          )}
                        </span>
                        {cameraInfo.selectedCameraId === camera.id && (
                          <span class="selected-indicator">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div class="device-info">
                    <small>Dispositivo: {cameraInfo.deviceType}</small>
                  </div>
                </div>
              )}

              {/* Botón de captura manual cuando el detector está deshabilitado (solo para el frente) */}
              {this.showManualCaptureButton && captureState.step === 'front' && !captureState.showFlipAnimation && !captureState.showSuccessAnimation && (
                <div class="manual-capture-section">
                  <button
                    class="manual-capture-button"
                    onClick={() => this.takeManualScreenshot()}
                    type="button"
                    disabled={this.processingButton !== null}
                  >
                    {this.processingButton === 'capture-front' && (
                      <span class="button-spinner"></span>
                    )}
                    Capturar Frente
                  </button>
                </div>
              )}
            </div>
          )}

          {captureState.isCapturing && (
            <div class="capture-animation"></div>
          )}

          {captureState.showFlipAnimation && (
            <div class="flip-animation">
              <div class="id-card-icon"></div>
              <div class="flip-text">¡Voltea tu identificación!</div>
            </div>
          )}

          {captureState.showSuccessAnimation && (
            <div class="success-animation">
              <div class="check-icon"></div>
              <div class="success-text">¡Proceso completado!</div>
            </div>
          )}

          {/* Estado siempre visible */}
          <div class={`component-status status-${this.currentStatus.type}`}>
              {(this.currentStatus.type === 'loading' || this.currentStatus.type === 'initializing') && (
                <div class="status-spinner"></div>
              )}
              <div class="status-content">
                <div class="status-message">{this.currentStatus.message}</div>
                {this.currentStatus.description && (
                  <div class="status-description">{this.currentStatus.description}</div>
                )}
              </div>
            </div>

          {/* Monitor de performance - solo visible en modo debug */}
          {this.debug && (
            <div class="performance-monitor">
              <div class="performance-expanded">
                <div class="metrics-row">
                  <div class="metric-compact">
                    <span class="metric-label">FPS</span>
                    <span class={`metric-value ${this.performanceData.fps < 15 ? 'warning' : this.performanceData.fps < 10 ? 'danger' : 'good'}`}>
                      {this.performanceData.fps}
                    </span>
                  </div>
                  <div class="metric-compact">
                    <span class="metric-label">MEM</span>
                    <span class={`metric-value ${this.performanceData.memoryUsage > 100 ? 'warning' : this.performanceData.memoryUsage > 200 ? 'danger' : 'good'}`}>
                      {this.performanceData.memoryUsage}MB
                    </span>
                  </div>
                </div>
                <div class="metrics-row">
                  <div class="metric-compact">
                    <span class="metric-label">INF</span>
                    <span class={`metric-value ${this.performanceData.inferenceTime > 100 ? 'warning' : this.performanceData.inferenceTime > 200 ? 'danger' : 'good'}`}>
                      {this.performanceData.inferenceTime}ms
                    </span>
                  </div>
                  <div class="metric-compact">
                    <span class="metric-label">FRAME</span>
                    <span class={`metric-value ${this.performanceData.frameProcessingTime > 50 ? 'warning' : this.performanceData.frameProcessingTime > 100 ? 'danger' : 'good'}`}>
                      {this.performanceData.frameProcessingTime}ms
                    </span>
                  </div>
                </div>
                <div class="metrics-row">
                  <div class="metric-compact">
                    <span class="metric-label">DET</span>
                    <span class="metric-value good">
                      {this.performanceData.successfulDetections}/{this.performanceData.totalDetections}
                    </span>
                  </div>
                  <div class="metric-compact">
                    <span class="metric-label">RATE</span>
                    <span class={`metric-value ${this.performanceData.detectionRate < 30 ? 'danger' : this.performanceData.detectionRate < 60 ? 'warning' : 'good'}`}>
                      {this.performanceData.detectionRate}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}


          <div class="watermark">
            <img
              src="https://static.jaak.ai/commons/powered-by-jaak.png"
              alt="Powered by Jaak"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.onerror = null;
                img.src = POWERED_BY_JAAK_FALLBACK;
              }}
            />
          </div>
        </div>
        )}
      </div>
    );
  }

  // Utility methods
  private updateDetectionBoxes(boxes: DetectionBox[]): void {
    if (!this.videoRef || !this.detectionContainer) return;

    const videoWidth = this.videoRef.videoWidth;
    const videoHeight = this.videoRef.videoHeight;
    const container = this.detectionContainer.parentElement!;
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    if (videoWidth === 0 || videoHeight === 0) return;

    const videoAspectRatio = videoWidth / videoHeight;
    const containerAspectRatio = containerWidth / containerHeight;

    let displayedVideoWidth: number, displayedVideoHeight: number;
    let videoOffsetX = 0, videoOffsetY = 0;

    if (videoAspectRatio > containerAspectRatio) {
      displayedVideoWidth = containerWidth;
      displayedVideoHeight = containerWidth / videoAspectRatio;
      videoOffsetY = (containerHeight - displayedVideoHeight) / 2;
    } else {
      displayedVideoHeight = containerHeight;
      displayedVideoWidth = containerHeight * videoAspectRatio;
      videoOffsetX = (containerWidth - displayedVideoWidth) / 2;
    }

    const INPUT_SIZE = 320;
    const scaleX = displayedVideoWidth / INPUT_SIZE;
    const scaleY = displayedVideoHeight / INPUT_SIZE;

    this.detectionBoxes = boxes.map(det => ({
      x: det.x * scaleX + videoOffsetX,
      y: det.y * scaleY + videoOffsetY,
      w: det.w * scaleX,
      h: det.h * scaleY,
      score: det.score
    }));
  }

  private updateMaskColor(boxes: DetectionBox[]): void {
    const cardOutline = this.el.shadowRoot?.querySelector('.card-outline');
    const corners = this.el.shadowRoot?.querySelectorAll('.corner');
    const topSide = this.el.shadowRoot?.querySelector('.side-top');
    const rightSide = this.el.shadowRoot?.querySelector('.side-right');
    const bottomSide = this.el.shadowRoot?.querySelector('.side-bottom');
    const leftSide = this.el.shadowRoot?.querySelector('.side-left');

    let bestBox = null;
    let currentAlignment = { top: false, right: false, bottom: false, left: false };

    if (boxes.length > 0) {
      bestBox = boxes.reduce((best, current) =>
        current.score > best.score ? current : best
      );

      const maskConfig = {
        INPUT_SIZE: 320,
        ID1_ASPECT_RATIO: 85.60 / 53.98,
        shouldMirrorVideo: this.shouldMirrorVideo,
        alignmentTolerance: this.alignmentTolerance,
        maskSize: this.maskSize,
        videoRef: this.videoRef
      };

      currentAlignment = this.detectionService.checkSideAlignment(bestBox, maskConfig);
      this.sideAlignment = currentAlignment;
    } else {
      this.sideAlignment = { top: false, right: false, bottom: false, left: false };
    }

    topSide?.classList.toggle('aligned', currentAlignment.top);
    rightSide?.classList.toggle('aligned', currentAlignment.right);
    bottomSide?.classList.toggle('aligned', currentAlignment.bottom);
    leftSide?.classList.toggle('aligned', currentAlignment.left);

    const allSidesAligned = this.detectionService.areAllSidesAligned(currentAlignment);

    // Restart back document timer if any border is aligned during back capture
    const captureState = this.stateManager.getCaptureState();
    if (captureState.step === 'back' && this.enableBackDocumentTimer && bestBox) {
      const anyBorderAligned = currentAlignment.top || currentAlignment.right ||
                              currentAlignment.bottom || currentAlignment.left;
      if (anyBorderAligned) {
        this.startBackDocumentTimer();
      }
    }

    if (allSidesAligned && bestBox) {
      cardOutline?.classList.add('perfect-match');
      corners?.forEach(corner => corner.classList.add('perfect-match'));


      if (!this.hasScreenshotTaken) {
        const currentTime = Date.now();

        // Initialize alignment start time if not set
        if (!this.alignmentStartTime) {
          this.alignmentStartTime = currentTime;
        }

        // Check if document has been aligned for the configured delay
        const alignmentDuration = currentTime - this.alignmentStartTime;

        if (alignmentDuration >= this.captureDelay) {
          this.lastDetectedBox = bestBox;
          this.takeScreenshot().catch(() => {
            // Handle screenshot error silently
          });
          this.hasScreenshotTaken = true;
          this.alignmentStartTime = undefined;

          setTimeout(() => {
            this.hasScreenshotTaken = false;
          }, 2000);
        }
      }
    } else {
      cardOutline?.classList.remove('perfect-match');
      corners?.forEach(corner => corner.classList.remove('perfect-match'));

      // Reset alignment timer when document moves out of position
      if (this.alignmentStartTime) {
        this.alignmentStartTime = undefined;
      }
      if (this.alignmentTimer) {
        clearTimeout(this.alignmentTimer);
        this.alignmentTimer = undefined;
      }
    }
  }

  private async takeManualScreenshot(): Promise<void> {
    console.log('🔵 Botón clicked: Capturar (Frente/Reverso)');

    const captureStartTime = performance.now();
    const span = this.tracingService?.startSpan('capture.screenshot.manual');

    // Set processing state immediately
    const captureState = this.stateManager?.getCaptureState();
    const side = captureState?.step === 'front' ? 'front' : 'back';

    if (captureState?.step === 'front') {
      this.processingButton = 'capture-front';
      if (span) this.tracingService?.setSpanAttribute(span, 'side', 'front');
    } else if (captureState?.step === 'back') {
      this.processingButton = 'capture-back';
      if (span) this.tracingService?.setSpanAttribute(span, 'side', 'back');
    }

    // Record user interaction
    this.metricsService?.recordUserInteraction('manual_capture_button');

    console.log('🔵 processingButton set to:', this.processingButton);

    // Add small delay to show spinner
    await new Promise(resolve => setTimeout(resolve, 100));

    if (!this.videoRef) {
      this.processingButton = null;
      if (span) {
        this.tracingService?.setSpanAttribute(span, 'error', 'no_video_ref');
        this.tracingService?.endSpan(span);
      }
      return;
    }

    // When using manual capture, use mask coordinates for cropping
    // Note: processingButton will be cleared inside takeScreenshotWithMaskCoordinates
    await this.takeScreenshotWithMaskCoordinates();

    // Record metrics
    const captureDuration = performance.now() - captureStartTime;
    this.metricsService?.recordCapture(side as 'front' | 'back', 'manual', captureDuration);

    if (span) {
      this.tracingService?.setSpanAttribute(span, 'success', true);
      this.tracingService?.endSpan(span);
    }
  }

  private async takeScreenshotWithMaskCoordinates(): Promise<void> {
    if (!this.videoRef || !this.detectionContainer) return;

    this.stateManager.updateCaptureState({ isCapturing: true });
    this.triggerCaptureAnimation();

    // Use pooled canvas for optimization
    const captureCanvas = this.getPooledCanvas(this.videoRef.videoWidth, this.videoRef.videoHeight);
    const captureCtx = captureCanvas.getContext('2d', { alpha: false })!;
    captureCtx.clearRect(0, 0, captureCanvas.width, captureCanvas.height);
    captureCtx.drawImage(this.videoRef, 0, 0, captureCanvas.width, captureCanvas.height);

    // Calculate mask coordinates for cropping
    const container = this.detectionContainer.parentElement!;
    const containerRect = container.getBoundingClientRect();

    // Get mask dimensions from CSS properties
    const maskWidthPercent = parseFloat(this.el.style.getPropertyValue('--mask-width').replace('%', '')) || 90;
    const maskHeightPercent = parseFloat(this.el.style.getPropertyValue('--mask-height').replace('%', '')) || 56.25;
    const maskCenterXPercent = parseFloat(this.el.style.getPropertyValue('--mask-center-x').replace('%', '')) || 50;
    const maskCenterYPercent = parseFloat(this.el.style.getPropertyValue('--mask-center-y').replace('%', '')) || 50;

    // Convert percentages to actual video coordinates
    const videoWidth = this.videoRef.videoWidth;
    const videoHeight = this.videoRef.videoHeight;
    const videoAspectRatio = videoWidth / videoHeight;
    const containerAspectRatio = containerRect.width / containerRect.height;

    let displayedVideoWidth: number, displayedVideoHeight: number;
    let videoOffsetX = 0, videoOffsetY = 0;

    if (videoAspectRatio > containerAspectRatio) {
      displayedVideoWidth = containerRect.width;
      displayedVideoHeight = containerRect.width / videoAspectRatio;
      videoOffsetY = (containerRect.height - displayedVideoHeight) / 2;
    } else {
      displayedVideoHeight = containerRect.height;
      displayedVideoWidth = containerRect.height * videoAspectRatio;
      videoOffsetX = (containerRect.width - displayedVideoWidth) / 2;
    }

    // Calculate mask coordinates in video space
    const maskWidthInContainer = (maskWidthPercent / 100) * containerRect.width;
    const maskHeightInContainer = (maskHeightPercent / 100) * containerRect.height;
    const maskCenterXInContainer = (maskCenterXPercent / 100) * containerRect.width;
    const maskCenterYInContainer = (maskCenterYPercent / 100) * containerRect.height;

    // Convert to video coordinates
    const scaleX = videoWidth / displayedVideoWidth;
    const scaleY = videoHeight / displayedVideoHeight;

    const maskCenterXInVideo = (maskCenterXInContainer - videoOffsetX) * scaleX;
    const maskCenterYInVideo = (maskCenterYInContainer - videoOffsetY) * scaleY;
    const maskWidthInVideo = maskWidthInContainer * scaleX;
    const maskHeightInVideo = maskHeightInContainer * scaleY;

    // Calculate crop coordinates
    const cropX = Math.max(0, maskCenterXInVideo - (maskWidthInVideo / 2) - this.cropMargin);
    const cropY = Math.max(0, maskCenterYInVideo - (maskHeightInVideo / 2) - this.cropMargin);
    const cropWidth = Math.min(maskWidthInVideo + (2 * this.cropMargin), videoWidth - cropX);
    const cropHeight = Math.min(maskHeightInVideo + (2 * this.cropMargin), videoHeight - cropY);

    // Use pooled canvas for cropped version
    const croppedCanvas = this.getPooledCanvas(cropWidth, cropHeight);
    const croppedCtx = croppedCanvas.getContext('2d', { alpha: false })!;
    croppedCtx.clearRect(0, 0, croppedCanvas.width, croppedCanvas.height);

    croppedCtx.drawImage(
      this.videoRef,
      cropX, cropY, cropWidth, cropHeight,
      0, 0, cropWidth, cropHeight
    );

    const captureState = this.stateManager.getCaptureState();

    if (captureState.step === 'front') {
      this.stateManager.setCapturedImages({
        front: {
          fullFrame: captureCanvas.toDataURL('image/jpeg'),
          cropped: croppedCanvas.toDataURL('image/jpeg')
        }
      });

      // Check if document classification is enabled (independent of detector)
      if (this.useDocumentClassification && !this.classificationDisabled) {
        try {
          const classification = await this.detectionService.classifyDocument(croppedCanvas);

          if (classification && classification.class === 'passport') {
            this.completeProcess(true);
            this.returnCanvasToPool(captureCanvas);
            this.returnCanvasToPool(croppedCanvas);
            // Clear processing button when passport is detected
            this.processingButton = null;
            return;
          }
        } catch (classifyError) {
          // No cambiar a manual aquí, solo registrar el error y continuar
        }
      }

      this.stateManager.updateCaptureState({
        step: 'back',
        isDetectionPaused: true,
        showFlipAnimation: true
      });

      this.triggerRerender();

      setTimeout(() => {
        this.stateManager.updateCaptureState({
          showFlipAnimation: false,
          isDetectionPaused: false
        });

        this.triggerRerender();
        this.startBackDocumentTimer();
        // Clear processing button after animation completes
        this.processingButton = null;
      }, 3000);

    } else if (captureState.step === 'back') {
      this.stateManager.setCapturedImages({
        back: {
          fullFrame: captureCanvas.toDataURL('image/jpeg'),
          cropped: croppedCanvas.toDataURL('image/jpeg')
        }
      });

      this.completeProcess(false);
      // Clear processing button after back capture completes
      this.processingButton = null;
    }

    // Return canvases to pool after use
    this.returnCanvasToPool(captureCanvas);
    this.returnCanvasToPool(croppedCanvas);
  }

  private async takeScreenshot(): Promise<void> {
    if (!this.videoRef || !this.lastDetectedBox) return;

    const captureStartTime = performance.now();
    const span = this.tracingService?.startSpan('capture.screenshot.auto');

    this.stateManager.updateCaptureState({ isCapturing: true });
    this.triggerCaptureAnimation();

    // Use pooled canvas for optimization
    const captureCanvas = this.getPooledCanvas(this.videoRef.videoWidth, this.videoRef.videoHeight);
    const captureCtx = captureCanvas.getContext('2d', { alpha: false })!;
    captureCtx.clearRect(0, 0, captureCanvas.width, captureCanvas.height);
    captureCtx.drawImage(this.videoRef, 0, 0, captureCanvas.width, captureCanvas.height);

    // Calculate crop coordinates
    const INPUT_SIZE = 320;
    const scaleX = this.videoRef.videoWidth / INPUT_SIZE;
    const scaleY = this.videoRef.videoHeight / INPUT_SIZE;

    const cropX = Math.max(0, (this.lastDetectedBox.x * scaleX) - this.cropMargin);
    const cropY = Math.max(0, (this.lastDetectedBox.y * scaleY) - this.cropMargin);
    const cropWidth = Math.min((this.lastDetectedBox.w * scaleX) + (2 * this.cropMargin), this.videoRef.videoWidth - cropX);
    const cropHeight = Math.min((this.lastDetectedBox.h * scaleY) + (2 * this.cropMargin), this.videoRef.videoHeight - cropY);

    // Use pooled canvas for cropped version
    const croppedCanvas = this.getPooledCanvas(cropWidth, cropHeight);
    const croppedCtx = croppedCanvas.getContext('2d', { alpha: false })!;
    croppedCtx.clearRect(0, 0, croppedCanvas.width, croppedCanvas.height);

    croppedCtx.drawImage(
      this.videoRef,
      cropX, cropY, cropWidth, cropHeight,
      0, 0, cropWidth, cropHeight
    );

    const captureState = this.stateManager.getCaptureState();

    if (captureState.step === 'front') {
      const frontImageData = captureCanvas.toDataURL('image/jpeg');
      const croppedImageData = croppedCanvas.toDataURL('image/jpeg');

      this.stateManager.setCapturedImages({
        front: {
          fullFrame: frontImageData,
          cropped: croppedImageData
        }
      });

      // Record metrics
      const captureDuration = performance.now() - captureStartTime;
      this.metricsService?.recordCapture('front', 'auto', captureDuration);
      this.metricsService?.recordImageSize('front', frontImageData.length);

      if (span) {
        this.tracingService?.setSpanAttribute(span, 'side', 'front');
      }

      // Check if document classification is enabled
      if (this.useDocumentClassification && !this.classificationDisabled) {
        try {
          const classification = await this.detectionService.classifyDocument(croppedCanvas);

          if (classification && classification.class === 'passport') {
            if (span) {
              this.tracingService?.setSpanAttribute(span, 'document.type', 'passport');
              this.tracingService?.endSpan(span);
            }
            this.completeProcess(true);
            return;
          }
        } catch (classifyError) {
          // No cambiar a manual aquí, solo registrar el error y continuar
        }
      }

      this.stateManager.updateCaptureState({
        step: 'back',
        isDetectionPaused: true,
        showFlipAnimation: true
      });

      if (span) {
        this.tracingService?.endSpan(span);
      }

      setTimeout(() => {
        this.stateManager.updateCaptureState({
          showFlipAnimation: false,
          isDetectionPaused: false
        });
        this.startBackDocumentTimer();
      }, 3000);

    } else if (captureState.step === 'back') {
      const backImageData = captureCanvas.toDataURL('image/jpeg');
      const croppedBackImageData = croppedCanvas.toDataURL('image/jpeg');

      this.stateManager.setCapturedImages({
        back: {
          fullFrame: backImageData,
          cropped: croppedBackImageData
        }
      });

      // Record metrics
      const captureDuration = performance.now() - captureStartTime;
      this.metricsService?.recordCapture('back', 'auto', captureDuration);
      this.metricsService?.recordImageSize('back', backImageData.length);

      if (span) {
        this.tracingService?.setSpanAttribute(span, 'side', 'back');
        this.tracingService?.endSpan(span);
      }

      this.completeProcess(false);
    }

    // Return canvases to pool after use
    this.returnCanvasToPool(captureCanvas);
    this.returnCanvasToPool(croppedCanvas);
  }

  private triggerCaptureAnimation(): void {
    const cardOutline = this.el.shadowRoot?.querySelector('.card-outline');
    cardOutline?.classList.add('capturing');

    setTimeout(() => {
      this.stateManager.updateCaptureState({ isCapturing: false });
      cardOutline?.classList.remove('capturing');
    }, 600);
  }

  // Método simplificado para cambio a modo manual
  private switchToManualMode(): void {
    // Only switch if detector is enabled and not already switched
    if (!this.useDocumentDetector || this.hasAutoSwitchedToManual) {
      return;
    }

    if (this.debug) {
      console.warn('[JAAK-DEBUG] switchToManualMode() called - PERFORMANCE DEGRADED');
      console.warn('[JAAK-DEBUG] Cause: Slow frames exceeded threshold');
      console.warn('[JAAK-DEBUG] slowFrameCount:', this.slowFrameCount, '| threshold:', this.PERFORMANCE_THRESHOLD_MS, 'ms | trigger:', this.SLOW_FRAMES_TO_TRIGGER);
    }

    // Detener todo el monitoreo de performance
    this.stopPerformanceMonitoring();

    // Liberar recursos de ONNX
    this.releaseOnnxResources();

    // Resetear máscara a blanco y limpiar detecciones
    this.resetMaskToWhite();
    this.detectionBoxes = [];

    this.hasAutoSwitchedToManual = true;
    this.performanceDegradedMode = true;
    this.showManualCaptureButton = true;
    this.showPerformanceMessage = true;

    // Hide performance message after 5 seconds
    setTimeout(() => {
      this.showPerformanceMessage = false;
    }, 5000);
  }

  // Método para cambiar a modo manual por errores de ONNX
  private switchToManualModeWithWarning(warningMessage: string): void {
    // Solo cambiar si el detector está habilitado y no se ha cambiado ya
    if (!this.useDocumentDetector || this.hasAutoSwitchedToManual) {
      return;
    }

    if (this.debug) {
      console.warn('[JAAK-DEBUG] switchToManualModeWithWarning() called');
      console.warn('[JAAK-DEBUG] Cause:', warningMessage);
      console.warn('[JAAK-DEBUG] deviceMemory:', (navigator as any).deviceMemory, '| hardwareConcurrency:', navigator.hardwareConcurrency);
    }

    // No necesitamos liberar recursos ONNX porque no se han cargado aún

    // Resetear máscara a blanco y limpiar detecciones
    this.resetMaskToWhite();
    this.detectionBoxes = [];

    // Cambiar a modo manual
    this.hasAutoSwitchedToManual = true;
    this.performanceDegradedMode = true;
    this.showManualCaptureButton = true;
    // No modificar @Prop directamente (immutable). hasAutoSwitchedToManual + performanceDegradedMode ya desactivan la deteccion.

    // Actualizar el estado con mensaje informativo (no error)
    this.updateStatus('Modo manual activado', warningMessage, 'active');

    // Continuar con la configuración de cámara
    this.continueWithCameraSetup();
  }

  private async continueWithCameraSetup(): Promise<void> {
    try {
      // Paso 2: Detectar y configurar dispositivos
      this.updateStatus('Configurando cámara...', 'Buscando dispositivos disponibles', 'loading');

      try {
        await this.cameraService.enumerateDevices();
      } catch (enumerateError) {
        throw new Error(`Error al enumerar dispositivos: ${enumerateError.message}`);
      }

      try {
        await this.updateCameraInfoWithAutofocus();
      } catch (cameraInfoError) {
        throw new Error(`Error al actualizar información de cámara: ${cameraInfoError.message}`);
      }

      // Paso 3: Configurar cámara seleccionada
      this.updateStatus('Ajustando cámara...', 'Configurando calidad de imagen', 'loading');

      let stream: MediaStream;
      try {
        stream = await this.cameraService.setupCamera();
      } catch (setupError) {
        throw new Error(`Error al configurar cámara: ${setupError.message}`);
      }

      // Paso 4: Inicializar video
      this.updateStatus('Captura manual', 'Posicione su documento y use el botón para capturar', 'active');

      try {
        await this.initializeVideoStream(stream);
      } catch (videoError) {
        throw new Error(`Error al inicializar video: ${videoError.message}`);
      }

      // Paso 5: Calibrar máscara
      if (this.detectionContainer) {
        const container = this.detectionContainer.parentElement!;
        const rect = container.getBoundingClientRect();
        this.updateMaskDimensions(rect);
      }

      // Finalizar
      this.startTime = Date.now();
      this.stateManager.updateCaptureState({ isLoading: false });

      // Mostrar botón manual (ya debe estar habilitado)
      this.showManualCaptureButton = true;

    } catch (error) {
      this.updateStatus('Error de cámara', error.message, 'error');
      throw error;
    }
  }

  private switchToManualModeWithError(errorMessage: string): void {

    // Solo cambiar si el detector está habilitado y no se ha cambiado ya
    if (!this.useDocumentDetector || this.hasAutoSwitchedToManual) {
      return;
    }

    if (this.debug) {
      console.error('[JAAK-DEBUG] switchToManualModeWithError() called');
      console.error('[JAAK-DEBUG] Cause:', errorMessage);
      console.error('[JAAK-DEBUG] deviceMemory:', (navigator as any).deviceMemory, '| hardwareConcurrency:', navigator.hardwareConcurrency);
    }

    // Liberar recursos de ONNX inmediatamente
    this.releaseOnnxResources();

    // Resetear máscara a blanco y limpiar detecciones
    this.resetMaskToWhite();
    this.detectionBoxes = [];

    // Cambiar a modo manual
    this.hasAutoSwitchedToManual = true;
    this.performanceDegradedMode = true;
    this.showManualCaptureButton = true;
    // No modificar @Prop directamente (immutable). hasAutoSwitchedToManual + performanceDegradedMode ya desactivan la deteccion.

    // Actualizar el estado con mensaje de error
    this.updateStatus('Modo manual activado', errorMessage, 'error');

    // Continuar con la configuración de cámara para mostrar el video
    this.continueWithCameraSetup().catch(() => {
      // Si falla la configuración de cámara, mantener el mensaje de error pero cambiar el estado
      setTimeout(() => {
        this.updateStatus('Captura manual', 'Use el botón para capturar', 'ready');
      }, 5000);
    });
  }

  private stopPerformanceMonitoring(): void {
    // Nota: No necesitamos limpiar más variables porque ya no se usarán
    // El sistema automáticamente saltará la lógica de performance una vez que hasAutoSwitchedToManual = true
  }

  // Método para liberar recursos de ONNX cuando se cambia a modo manual
  private releaseOnnxResources(): void {

    try {
      // Liberar recursos del servicio de detección
      if (this.detectionService) {
        this.detectionService.cleanup();
      }
    } catch (error) {
    }
  }

  private async completeProcess(skippedBack: boolean = false): Promise<void> {
    this.stateManager.updateCaptureState({
      step: 'completed',
      showSuccessAnimation: true
    });

    const capturedImages = this.stateManager.getCapturedImages();
    capturedImages.metadata.processCompleted = true;
    capturedImages.metadata.backCaptureSkipped = skippedBack;
    this.stateManager.setCapturedImages(capturedImages);

    this.stopDetection();
    this.updateStatus('Proceso completado', `Imágenes capturadas exitosamente`, 'ready');

    const finalImages = {
      ...capturedImages,
      timestamp: new Date().toISOString()
    };

    // Flush all traces to collector before emitting capture completed
    if (this.tracingService) {
      try {
        await this.tracingService.flush();
      } catch (error) {
        console.error('[my-component] Failed to flush traces:', error);
      }
    }

    this.captureCompleted.emit(finalImages);

    setTimeout(() => {
      this.stateManager.updateCaptureState({ showSuccessAnimation: false });
    }, 3000);

  }

  private stopDetection(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = undefined;
    }
    this.clearBackDocumentTimer();

    this.detectionBoxes = [];
  }

  private startBackDocumentTimer(): void {
    if (!this.enableBackDocumentTimer) return;

    if (this.backDocumentTimer) {
      clearInterval(this.backDocumentTimer);
    }

    this.backDocumentTimerRemaining = this.backDocumentTimerDuration;

    this.backDocumentTimer = window.setInterval(() => {
      this.backDocumentTimerRemaining--;

      if (this.backDocumentTimerRemaining <= 0) {
        this.clearBackDocumentTimer();
        this.skipBackCapture();
      }
    }, 1000);
  }

  private clearBackDocumentTimer(): void {
    if (this.backDocumentTimer) {
      clearInterval(this.backDocumentTimer);
      this.backDocumentTimer = undefined;
    }
    this.backDocumentTimerRemaining = 0;
  }


  private toggleCameraSelector(): void {
    if (this.isSwitchingCamera) return; // Don't toggle if switching camera
    this.showCameraSelector = !this.showCameraSelector;
  }

  private async updateCameraInfoWithAutofocus() {
    try {
      const cameraInfo = await this.cameraService.getCameraInfo();
      this.cameraInfoWithAutofocus = cameraInfo;
    } catch (error) {
      // Keep existing state if update fails
    }
  }

  private async handleCameraSwitch(cameraId: string): Promise<void> {
    if (this.isSwitchingCamera) return; // Prevent multiple simultaneous switches

    try {
      // Close the selector immediately when user selects a camera
      this.showCameraSelector = false;

      // Check if user selected the same camera that's already active
      const currentCameraId = this.cameraService.getSelectedCameraId();
      if (currentCameraId === cameraId) {
        // Same camera selected, just close the selector without switching
        return;
      }

      this.isSwitchingCamera = true;

      // Stop current video stream
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop());
      }

      // Switch camera in service
      await this.cameraService.switchCamera(cameraId);

      // Setup new camera stream
      const newStream = await this.cameraService.setupCamera();

      // Update video element
      await this.initializeVideoStream(newStream);

      // Update camera info with autofocus data
      await this.updateCameraInfoWithAutofocus();

    } catch (error) {
      // Try to restore previous camera if switch failed
      try {
        const fallbackStream = await this.cameraService.setupCamera();
        await this.initializeVideoStream(fallbackStream);
      } catch (fallbackError) {
        this.updateStatus('Error al cambiar cámara', 'No se pudo cambiar el dispositivo', 'error');
      }
    } finally {
      this.isSwitchingCamera = false;
    }
  }

  private resetDetection(): void {
    const currentCaptureState = this.stateManager.getCaptureState();
    const wasVideoActive = currentCaptureState.isVideoActive;

    this.stateManager.reset();

    if (wasVideoActive) {
      this.stateManager.updateCaptureState({ isVideoActive: true });
    }

    this.hasScreenshotTaken = false;
    this.startTime = Date.now();
    this.frameSkipCounter = 0;
    this.consecutiveFailures = 0;
    this.lastInferenceTime = 0;
    this.detectionBoxes = [];
    this.alignmentStartTime = undefined;
    this.hasDocumentDetected = false;

    // Reset sistema simplificado
    this.processedFramesCount = 0;
    this.slowFrameCount = 0;

    // Reset performance degradation state
    this.hasAutoSwitchedToManual = false;
    this.performanceDegradedMode = false;
    this.showPerformanceMessage = false;

    if (this.alignmentTimer) {
      clearTimeout(this.alignmentTimer);
      this.alignmentTimer = undefined;
    }

    if (wasVideoActive && this.detectionService.isModelLoaded()) {
      this.updateStatus('Captura reiniciada', 'Buscando documento en el marco de captura', 'active');
      this.detectFrame();
    } else {
      this.updateStatus('Listo para capturar', '', 'ready');
    }
  }

  private exitSession(): void {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = undefined;
      this.stateManager.updateCaptureState({ isVideoActive: false, isLoading: false });
    }

    this.isMaskReady = false;
    this.updateStatus('Captura finalizada', '', 'ready');
    this.detectionBoxes = [];
    this.cleanup();
  }

  // PERFORMANCE MONITORING METHODS
  private initializePerformanceMonitor(): void {
    this.performanceMetrics.lastUpdateTime = performance.now();

    // Update performance metrics every 500ms
    this.performanceUpdateInterval = window.setInterval(() => {
      this.updatePerformanceMetrics();
    }, 500);

  }

  private updatePerformanceMetrics(): void {
    const currentTime = performance.now();
    const deltaTime = currentTime - this.performanceMetrics.lastUpdateTime;

    // Calculate FPS
    if (deltaTime > 0) {
      this.performanceMetrics.fps = Math.round(1000 / (deltaTime / this.frameSkipCounter || 1));
    }

    // Get memory usage if available
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      this.performanceMetrics.memoryUsage = Math.round(memInfo.usedJSHeapSize / 1048576); // MB
    }

    // Calculate detection success rate
    if (this.performanceMetrics.totalDetections > 0) {
      this.performanceMetrics.successfulDetections = this.performanceMetrics.successfulDetections;
      const detectionRate = (this.performanceMetrics.successfulDetections / this.performanceMetrics.totalDetections) * 100;
      this.performanceMetrics.detectionRate = Math.round(detectionRate);
    }

    // Update state for rendering
    this.performanceData = {
      fps: this.performanceMetrics.fps,
      inferenceTime: this.performanceMetrics.inferenceTime,
      memoryUsage: this.performanceMetrics.memoryUsage,
      onnxLoadTime: this.performanceMetrics.onnxLoadTime,
      frameProcessingTime: this.performanceMetrics.frameProcessingTime,
      totalDetections: this.performanceMetrics.totalDetections,
      successfulDetections: this.performanceMetrics.successfulDetections,
      detectionRate: this.performanceMetrics.detectionRate
    };

    this.performanceMetrics.lastUpdateTime = currentTime;
  }

  private recordOnnxPerformance(loadTime: number, inferenceTime: number): void {
    this.performanceMetrics.onnxLoadTime = Math.round(loadTime);
    this.performanceMetrics.inferenceTime = Math.round(inferenceTime);
  }

  private recordFrameProcessing(processingTime: number, detectionsFound: number): void {
    this.performanceMetrics.frameProcessingTime = Math.round(processingTime);
    this.performanceMetrics.totalDetections++;

    if (detectionsFound > 0) {
      this.performanceMetrics.successfulDetections++;
    }

    // Update performance history for adaptive frame skipping
    this.performanceHistory.push(processingTime);
    if (this.performanceHistory.length > this.PERFORMANCE_HISTORY_SIZE) {
      this.performanceHistory.shift();
    }
  }

  // Adaptive frame skipping based on performance
  private getAdaptiveFrameSkip(): number {
    // Check if running on mobile device for more aggressive frame skipping
    const isMobileDevice = this.cameraInfoWithAutofocus.deviceType === 'mobile' ||
                           /Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isTablet = this.cameraInfoWithAutofocus.deviceType === 'tablet' ||
                     (/iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768);

    // Mobile-specific base frame skip (more aggressive to prevent slow motion effect)
    const mobileBaseSkip = isMobileDevice && !isTablet ? 4 : this.BASE_FRAME_SKIP;

    // Base conditions for high frame skip
    if (this.consecutiveFailures > 20) return this.MAX_FRAME_SKIP;
    if (this.performanceMetrics.inferenceTime > 200) return Math.max(6, mobileBaseSkip);
    if (this.performanceMetrics.memoryUsage > 200) return Math.max(5, mobileBaseSkip);

    // Calculate average processing time
    if (this.performanceHistory.length > 0) {
      const avgProcessingTime = this.performanceHistory.reduce((a, b) => a + b, 0) / this.performanceHistory.length;

      if (avgProcessingTime > 100) return Math.max(4, mobileBaseSkip);
      if (avgProcessingTime > 80) return Math.max(3, mobileBaseSkip);
      if (avgProcessingTime > 60) return Math.max(this.BASE_FRAME_SKIP + 1, mobileBaseSkip);
    }

    // Low memory devices get higher frame skip
    if (this.performanceMetrics.memoryUsage > 150) return Math.max(4, mobileBaseSkip);

    // Mobile devices use higher base frame skip for smoother video
    return mobileBaseSkip;
  }

  // Método para resetear la máscara a color blanco
  private resetMaskToWhite(): void {
    const cardOutline = this.el.shadowRoot?.querySelector('.card-outline');
    const corners = this.el.shadowRoot?.querySelectorAll('.corner');
    const topSide = this.el.shadowRoot?.querySelector('.side-top');
    const rightSide = this.el.shadowRoot?.querySelector('.side-right');
    const bottomSide = this.el.shadowRoot?.querySelector('.side-bottom');
    const leftSide = this.el.shadowRoot?.querySelector('.side-left');

    // Remover todas las clases de alineación y estado perfecto
    cardOutline?.classList.remove('perfect-match');
    corners?.forEach(corner => corner.classList.remove('perfect-match'));

    topSide?.classList.remove('aligned');
    rightSide?.classList.remove('aligned');
    bottomSide?.classList.remove('aligned');
    leftSide?.classList.remove('aligned');

    // Resetear estado de alineación
    this.sideAlignment = { top: false, right: false, bottom: false, left: false };
  }

  // Canvas pool management for screenshots
  private getPooledCanvas(width: number, height: number): HTMLCanvasElement {
    // Try to find a canvas with matching dimensions
    const matchingIndex = this.canvasPool.findIndex(canvas =>
      canvas.width === width && canvas.height === height
    );

    if (matchingIndex !== -1) {
      return this.canvasPool.splice(matchingIndex, 1)[0];
    }

    // If no matching canvas, try to reuse any canvas and resize
    if (this.canvasPool.length > 0) {
      const canvas = this.canvasPool.pop()!;
      canvas.width = width;
      canvas.height = height;
      return canvas;
    }

    // Create new canvas if pool is empty
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  private returnCanvasToPool(canvas: HTMLCanvasElement): void {
    // Only return to pool if not at max capacity
    if (this.canvasPool.length < this.MAX_CANVAS_POOL_SIZE) {
      // Clear the canvas before returning to pool
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.canvasPool.push(canvas);
    }
    // If pool is full, let the canvas be garbage collected
  }

}
